'use client';

import UiIcon from '@/components/ui/UiIcon';
import { memo, useLayoutEffect, useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { createPortal, flushSync } from 'react-dom';
import { API_URL } from '@/lib/api';
import { getAccessToken } from '@/lib/authStorage';
import { getCachedPhoto, putPhotoToCache } from '@/utils/chatPhotoCache';
import {
  formatMessageText,
  formatMessageTime,
  formatFullDateTime,
  MessageStatus,
  getMessageStatusIcon,
  extractFirstUrl,
} from '@/utils/messageUtils';
import { WAVE_BARS_COUNT, getCachedVoiceWave, prefetchVoiceWave } from '@/utils/voiceWave';
import {
  deleteCachedVoice,
  downloadVoiceToCache,
  getCachedVoiceBlob,
  isOnline,
  resolveVoiceUrl,
} from '@/utils/voiceFileCache';
import { logVoiceError, logNetworkError } from '@/utils/errorLogger';
import { AvatarFx } from '@/components/chat/AvatarFx';
import { optimizeImage } from '@/lib/imageUtils';
import { DEFAULT_MESSENGER_REACTIONS, buildMessengerReactionsFromSettings, fetchEmojiPacks } from '@/utils/emojiPacks';
import { fetchLinkPreview as fetchLinkPreviewClient } from '@/utils/linkPreviewClient';
import { readXlsxPreviewInWorker } from '@/lib/projectPlanExchangeClient';
import mammoth from 'mammoth';

// Комментарий на русском: глобальный “менеджер” воспроизведения — в один момент играет только одно голосовое.
// Зачем: при старте нового — останавливаем предыдущее, чтобы не было какофонии.
let activeVoiceAudio: HTMLAudioElement | null = null;
// Комментарий на русском: ограничиваем автоскачивание голосовых в CacheStorage по rank.
// Зачем: префетч волн до 100 — ок, но скачивать 100 аудио файлов сразу = лишняя нагрузка на сеть/диск.
const VOICE_FILE_PREFETCH_RANK_LIMIT = 20;

// Комментарий на русском: общий “сигнал сети” (online/offline) для retry загрузки медиа.
// Зачем: если соединение отвалилось в момент загрузки фото/аудио, компонент мог остаться с пустым src навсегда.
let _netTick = 0;
let _netInit = false;
const _netSubs = new Set<() => void>();
function _ensureNetInit() {
  if (_netInit) return;
  _netInit = true;
  if (typeof window === 'undefined') return;
  const bump = () => {
    _netTick++;
    // Комментарий на русском: TS target=es5, поэтому не используем for..of по Set (нужен downlevelIteration).
    _netSubs.forEach((fn) => {
      try {
        fn();
      } catch {}
    });
  };
  try {
    window.addEventListener('online', bump);
    window.addEventListener('offline', bump);
  } catch {}
}
function _subscribeNetTick(fn: () => void): () => void {
  _ensureNetInit();
  _netSubs.add(fn);
  return () => {
    _netSubs.delete(fn);
  };
}
// Комментарий на русском: "разблокировка" аудио-проигрывания для iOS/Safari (user gesture).
// Зачем: если play() вызывается после async (скачивание) — iOS может блокировать звук.
let voicePlaybackUnlocked = false;
const SILENT_WAV =
  'data:audio/wav;base64,UklGRiYAAABXQVZFZm10IBAAAAABAAEAQB8AAIA+AAACABAAZGF0YQIAAAAAAA=='; // 46 bytes, 1 sample (тишина)

function unlockVoicePlaybackOnce() {
  if (voicePlaybackUnlocked) return;
  try {
    voicePlaybackUnlocked = true;
    const a = new Audio();
    a.src = SILENT_WAV;
    // Комментарий на русском: не используем muted=true, чтобы “разблокировка” распространялась на обычный звук.
    a.muted = false;
    a.volume = 0;
    const p = a.play();
    if (p && typeof (p as any).then === 'function') {
      (p as any)
        .then(() => {
          try {
            a.pause();
          } catch {}
        })
        .catch(() => {
          voicePlaybackUnlocked = false;
        });
    }
  } catch {
    voicePlaybackUnlocked = false;
  }
}

function isIOSStandaloneApp(): boolean {
  // Комментарий на русском: iOS PWA (режим standalone) живёт в отдельном WebView и чаще ломает аудио/CacheStorage.
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (ua.includes('Macintosh') && typeof document !== 'undefined' && 'ontouchend' in document);
  if (!isIOS) return false;
  const mm = typeof window.matchMedia === 'function' ? window.matchMedia('(display-mode: standalone)') : null;
  return !!(mm?.matches || (navigator as any)?.standalone);
}

// Комментарий на русском: быстрые реакции берём из emoji pack-ов (настраивается в админке).
let _cachedQuickReactions: string[] | null = null;
let _quickReactionsInflight: Promise<string[]> | null = null;

async function loadQuickReactions(): Promise<string[]> {
  if (_cachedQuickReactions) return _cachedQuickReactions;
  if (_quickReactionsInflight) return _quickReactionsInflight;

  _quickReactionsInflight = (async () => {
    try {
      const [packs, settings] = await Promise.all([
        fetchEmojiPacks(),
        fetch(`${API_URL}/api/settings/`, { cache: 'no-store' }).then((r) => (r.ok ? r.json() : null)).catch(() => null),
      ]);
      const list = buildMessengerReactionsFromSettings(packs, settings);
      _cachedQuickReactions = list;
      return list;
    } catch {
      const fallback = Array.from(DEFAULT_MESSENGER_REACTIONS);
      _cachedQuickReactions = fallback;
      return fallback;
    } finally {
      _quickReactionsInflight = null;
    }
  })();

  return _quickReactionsInflight;
}
// Комментарий на русском: “тяжесть” свайпа. 10 = в 10 раз тяжелее (больше сопротивление).
const SWIPE_WEIGHT = 10;
// Комментарий на русском: порог ответа считаем по движению пальца (raw), чтобы “ответить” можно было лёгким свайпом.
const SWIPE_REPLY_THRESHOLD = 48;
// Комментарий на русском: кэш предпросмотров ссылок (только на клиенте, перезапускается при перезагрузке вкладки).
type LinkPreview = {
  title?: string;
  description?: string;
  image?: string;
  url: string;
  domain: string;
  kind?: 'site' | 'video';
};
const linkPreviewCache = new Map<string, LinkPreview>();
const LINK_IMAGE_MAX_BYTES = 200 * 1024; // Комментарий на русском: лимит для сжатия картинок предпросмотра (200 КБ).

interface User {
  id: number;
  username: string;
  display_name: string;
  avatar_url?: string;
  avatar_fx_color?: string | null;
  avatar_fx_thickness?: number | null;
  avatar_fx_glow?: number | null;
  // Комментарий на русском: цвет сообщений автора (его выбирает сам пользователь; виден другим).
  bubble_color?: string | null;
}

interface Message {
  id: number;
  from_user: User;
  to_user: User;
  message: string;
  created_at: string;
  is_read: boolean;
  status?: MessageStatus;
  // Комментарий на русском: если true — предпросмотр ссылок для этого сообщения отключён (пользователь закрыл превью перед отправкой).
  link_preview_disabled?: boolean;
  // Комментарий на русском: reply_to приходит с бэка компактным объектом (для цитаты).
  reply_to?: {
    id: number;
    from_user?: User | null;
    message?: string;
    type?: string;
    voice_url?: string | null;
    voice_mime?: string | null;
    created_at?: string | null;
  } | null;
  // Комментарий на русском: реакции приходят агрегированные (emoji/count/me).
  reactions?: { emoji: string; count: number; me: boolean }[];
  // Комментарий на русском: для голосовых сообщений (могут приходить как URL из БД или как realtime __voice__:...).
  type?: string;
  // Комментарий на русском: новый формат голосовых — отдельный URL, чтобы не хранить ссылку в message.
  voice_url?: string | null;
  voice_mime?: string | null;
  local_voice_src?: string | null;
}

interface MessageBubbleProps {
  message: Message;
  isOwn: boolean;
  showAvatar?: boolean;
  // Комментарий на русском: онлайн/офлайн собеседника (для подсветки аватара эффектом).
  peerOnline?: boolean;
  isGrouped?: boolean;
  // Комментарий на русском: ID чата для постоянного кэширования фото (dm:123 или room:456).
  chatId?: string;
  // Комментарий на русском: если передали — открываем фото во внешнем viewer (с пролистыванием по чату).
  onOpenPhotoViewer?: (photoUrl: string, messageId: number) => void;
  onDelete?: (messageId: number, forAll: boolean) => void;
  // Комментарий на русском: свайп по сообщению -> ответ.
  onReply?: (message: Message) => void;
  // Комментарий на русском: реакция на сообщение (эмодзи).
  onReact?: (messageId: number, emoji: string) => void;
  // Комментарий на русском: режим выбора сообщений (multi-select) для пересылки/массовых действий.
  selectionMode?: boolean;
  // Комментарий на русском: это сообщение выбрано пользователем.
  isSelected?: boolean;
  // Комментарий на русском: переключение выбранности сообщения.
  onToggleSelect?: (message: Message) => void;
  // Комментарий на русском: переслать сообщение (UI выбора получателя — на уровне страницы).
  onForward?: (message: Message) => void;
  // Комментарий на русском: редактирование текста сообщения (доступно только для своих текстовых).
  onEdit?: (message: Message) => void;
  // Комментарий на русском: закрепить/открепить сообщение (логика на уровне страницы/чата).
  onPin?: (message: Message) => void;
  // Комментарий на русском: признак "сообщение закреплено" (для текста кнопки).
  isPinned?: boolean;
  // Комментарий на русском: "превью ссылок" = преобразование URL в кликабельные ссылки.
  linkify?: boolean;
  // Комментарий на русском: включение загрузки предпросмотра (картинка/title). Ссылки кликабельны всегда.
  enableLinkPreview?: boolean;
  // Комментарий на русском: префетч/расчёт waveform заранее (используем для последних голосовых в открытом чате).
  prefetchVoice?: boolean;
  // Комментарий на русском: приоритет префетча (0 = самое новое, 4 = пятое по свежести).
  prefetchRank?: number;
}

/**
 * Компонент пузырька сообщения с XSS защитой и статусами.
 * Мемоизирован для оптимизации производительности.
 */
const MessageBubble = memo(function MessageBubble({
  message,
  isOwn,
  showAvatar = true,
  peerOnline = false,
  isGrouped = false,
  chatId = '',
  onOpenPhotoViewer,
  onDelete,
  onReply,
  onReact,
  selectionMode = false,
  isSelected = false,
  onToggleSelect,
  onForward,
  onEdit,
  onPin,
  isPinned = false,
  linkify = true,
  enableLinkPreview = true,
  prefetchVoice = false,
  prefetchRank = 99,
}: MessageBubbleProps) {
  const hexToTriplet = useCallback((hex: unknown): string => {
    const s = String(hex || '').trim().toLowerCase();
    if (!/^#[0-9a-f]{6}$/.test(s)) return '';
    const r = parseInt(s.slice(1, 3), 16) || 0;
    const g = parseInt(s.slice(3, 5), 16) || 0;
    const b = parseInt(s.slice(5, 7), 16) || 0;
    return `${r} ${g} ${b}`;
  }, []);

  const [showMenu, setShowMenu] = useState(false);
  const [showReactions, setShowReactions] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [quickReactions, setQuickReactions] = useState<string[]>(() => Array.from(DEFAULT_MESSENGER_REACTIONS));
  const bubbleRef = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const reactionsMenuRef = useRef<HTMLDivElement | null>(null);
  const [menuPos, setMenuPos] = useState<{ left: number; top: number } | null>(null);
  const [reactionsMenuPos, setReactionsMenuPos] = useState<{ left: number; top: number } | null>(null);
  const pressTRef = useRef<number | null>(null);
  const scaleTRef = useRef<number | null>(null);
  const pressActiveRef = useRef(false);
  const startXRef = useRef(0);
  const startYRef = useRef(0);
  const swipeAllowedRef = useRef(false);
  // Комментарий на русском: на iOS ранний setPointerCapture (на pointerdown) часто даёт “поддёргивания” скролла.
  // Поэтому захватываем pointer ТОЛЬКО когда реально распознали горизонтальный свайп.
  const swipeCaptureIdRef = useRef<number | null>(null);
  const swipeRawRef = useRef(0);
  const swipeArmedRef = useRef(false);
  const swipeXRef = useRef(0);
  const [swipeX, setSwipeXState] = useState(0);
  const [swipeRaw, setSwipeRawState] = useState(0);
  const [swipeDown, setSwipeDownState] = useState(false);
  // Комментарий на русском: refs для проверки текущих значений — не вызываем setState если значение не меняется.
  const swipeDownRef = useRef(false);
  const swipeRawStateRef = useRef(0);
  const setSwipeDown = useCallback((v: boolean) => {
    if (swipeDownRef.current === v) return;
    swipeDownRef.current = v;
    setSwipeDownState(v);
  }, []);
  const setSwipeRaw = useCallback((v: number) => {
    if (swipeRawStateRef.current === v) return;
    swipeRawStateRef.current = v;
    setSwipeRawState(v);
  }, []);
  const setSwipe = useCallback((v: number) => {
    swipeXRef.current = v;
    setSwipeXState(v);
  }, []);
  const [pressScale, setPressScaleState] = useState(false);
  // Комментарий на русском: ref для проверки текущего значения — не вызываем setState если значение не меняется.
  // Это критично для Virtuoso — лишние ререндеры при скролле вызывают перемерку высот и прыжки.
  const pressScaleRef = useRef(false);
  const setPressScale = useCallback((v: boolean) => {
    if (pressScaleRef.current === v) return;
    pressScaleRef.current = v;
    setPressScaleState(v);
  }, []);

  useEffect(() => {
    // Комментарий на русском: чистим таймер, чтобы не было setState после unmount.
    return () => {
      if (pressTRef.current != null) window.clearTimeout(pressTRef.current);
      if (scaleTRef.current != null) window.clearTimeout(scaleTRef.current);
      if (photoPressTRef.current != null) window.clearTimeout(photoPressTRef.current);
    };
  }, []);

  useEffect(() => {
    // Комментарий на русском: один раз подгружаем актуальный набор быстрых реакций.
    let alive = true;
    void loadQuickReactions().then((list) => {
      if (alive && Array.isArray(list) && list.length) setQuickReactions(list);
    });
    return () => {
      alive = false;
    };
  }, []);

  const rawText = message?.message || '';
  const anyMsg = message as any;
  const reply = anyMsg?.reply_to && typeof anyMsg.reply_to === 'object' ? (anyMsg.reply_to as any) : null;
  const reactions = Array.isArray(anyMsg?.reactions) ? (anyMsg.reactions as any[]) : [];
  const hasReactions = reactions.length > 0;
  const attachments = useMemo(() => (Array.isArray(anyMsg?.attachments) ? (anyMsg.attachments as any[]) : []), [anyMsg?.attachments]);
  const voiceUrlRaw = typeof anyMsg?.voice_url === 'string' ? String(anyMsg.voice_url) : '';
  // Комментарий на русском: иногда (после пересылки) voice_url может прийти "грязным" текстом со ссылкой внутри.
  // Поэтому вытаскиваем реальную ссылку на файл, иначе voiceSrc будет пустым и плеер не найдёт источник.
  const voiceUrl = useMemo(() => {
    const t = String(voiceUrlRaw || '').trim();
    if (!t) return '';
    if (t.startsWith('/') || t.startsWith('blob:') || t.startsWith('data:') || /^https?:\/\//.test(t)) return t;
    const u = extractFirstUrl(t) || '';
    if (u && u.includes('/chat_voice/')) return u;
    return '';
  }, [voiceUrlRaw]);
  // Комментарий на русском: MIME приходит с бэка (guessVoiceMimeFromUrl), используем как подсказку для корректного blob: типа.
  const voiceMime = typeof anyMsg?.voice_mime === 'string' ? String(anyMsg.voice_mime) : '';
  const isRealtimeVoice = rawText.startsWith('__voice__:');
  const effective = voiceUrl || rawText;
  const isStoredVoice =
    effective.startsWith('/uploads/chat_voice/') ||
    effective.startsWith('/media/chat_voice/') ||
    (/^https?:\/\//.test(effective) && (effective.includes('/uploads/chat_voice/') || effective.includes('/media/chat_voice/')));
  const isBlobVoice = effective.startsWith('blob:');
  const isVoice = isRealtimeVoice || isStoredVoice || isBlobVoice || anyMsg?.type === 'voice_message' || !!voiceUrl;

  // ===== Фото (вложения) =====
  const photoUrlRaw = useMemo(() => {
    for (const a of attachments) {
      if (!a) continue;
      const kind = String((a as any)?.kind || '').toLowerCase();
      const mime = String((a as any)?.mime || '').toLowerCase();
      const url = String((a as any)?.url || '').trim();
      if (!url) continue;
      // Комментарий на русском: в группах часто приходит kind=file, но mime=image/* — тоже считаем фото.
      const isPhoto = kind === 'photo' || kind === 'image' || mime.startsWith('image/') || /\.(jpg|jpeg|png|gif|webp|avif)$/i.test(url);
      if (isPhoto) return url;
    }
    return '';
  }, [attachments]);

  // Комментарий на русском: фикс “прыжков” на iOS — держим корректный aspect-ratio до загрузки фото.
  const photoMeta = useMemo(() => {
    try {
      const a = (attachments || []).find((x: any) => {
        const kind = String(x?.kind || '').toLowerCase();
        const mime = String(x?.mime || '').toLowerCase();
        const url = String(x?.url || '');
        return kind === 'photo' || kind === 'image' || mime.startsWith('image/') || /\.(jpg|jpeg|png|gif|webp|avif)$/i.test(url);
      }) as any;
      const w = Number(a?.width || 0) || 0;
      const h = Number(a?.height || 0) || 0;
      return w > 0 && h > 0 ? { w, h } : null;
    } catch {
      return null;
    }
  }, [attachments]);
  const photoAspectStyle = useMemo(
    () => (photoMeta ? ({ aspectRatio: `${photoMeta.w}/${photoMeta.h}` } as any) : undefined),
    [photoMeta]
  );

  const resolveMediaUrl = useCallback((src: string) => {
    const u = String(src || '').trim();
    if (!u || u.startsWith('blob:') || u.startsWith('data:') || /^https?:\/\//.test(u)) return u;
    // Комментарий на русском: поддерживаем относительные пути и без ведущего "/".
    if (u.startsWith('/')) return `${API_URL}${u}`;
    return `${API_URL}/${u}`.replace(/\/{2,}/g, '/').replace(':/', '://');
  }, []);

  const photoUrl = useMemo(() => resolveMediaUrl(photoUrlRaw), [photoUrlRaw, resolveMediaUrl]);
  const isPhoto = !!photoUrlRaw;
  const fileAttRaw = useMemo(() => {
    for (const a of attachments) {
      if (!a) continue;
      const kind = String((a as any)?.kind || '').toLowerCase();
      const mime = String((a as any)?.mime || '').toLowerCase();
      const url = String((a as any)?.url || '').trim();
      if (!url && kind !== 'file') continue;
      const isPhotoLike = kind === 'photo' || kind === 'image' || mime.startsWith('image/') || /\.(jpg|jpeg|png|gif|webp|avif)$/i.test(url);
      if (isPhotoLike) continue;
      if (kind === 'file' || kind === 'document' || kind === 'archive' || url.includes('/api/chat/media/')) {
        return {
          url,
          mime: String((a as any)?.mime || '').trim(),
          size: Number((a as any)?.size || 0) || 0,
          name: String((a as any)?.name || (a as any)?.original_name || '').trim(),
        };
      }
    }
    return null;
  }, [attachments]);
  const fileUrl = useMemo(() => resolveMediaUrl(String(fileAttRaw?.url || '')), [fileAttRaw?.url, resolveMediaUrl]);
  const isFile = !!fileAttRaw;
  const isMedia = isPhoto || isFile; // Комментарий на русском: медиа (фото/файлы) — время рисуем внутри контента.
  const localPhotoSrc = typeof anyMsg?.local_photo_src === 'string' ? String(anyMsg.local_photo_src) : '';
  const [photoSrc, setPhotoSrc] = useState<string>('');
  const [isPhotoLoading, setIsPhotoLoading] = useState(false);
  const [photoNeedsDownload, setPhotoNeedsDownload] = useState(false);
  // Комментарий на русском: триггер для retry, когда сеть вернулась.
  const [netTickPhoto, setNetTickPhoto] = useState(0);
  const photoObjUrlRef = useRef<string>('');
  const photoKeyRef = useRef<string>('');

  // Комментарий на русском: встроенный просмотр фото (как lightbox, без window.open).
  const [photoViewerOpen, setPhotoViewerOpen] = useState(false);
  const [photoViewerDragY, setPhotoViewerDragY] = useState(0);
  const [photoViewerDragging, setPhotoViewerDragging] = useState(false);
  const photoViewerPointerIdRef = useRef<number | null>(null);
  const photoViewerStartRef = useRef<{ x: number; y: number } | null>(null);
  const photoViewerDragYRef = useRef<number>(0);
  const photoViewerOpenRef = useRef<boolean>(false);
  // Комментарий на русском: зум пальцами (pinch-to-zoom).
  const [photoViewerScale, setPhotoViewerScale] = useState(1);
  const [photoViewerTranslate, setPhotoViewerTranslate] = useState({ x: 0, y: 0 });
  const photoViewerPinchRef = useRef<{ distance: number; center: { x: number; y: number }; initialScale: number; initialTranslate: { x: number; y: number } } | null>(null);
  const photoViewerImgRef = useRef<HTMLImageElement | null>(null);
  // Комментарий на русском: “мобильный” режим только для телефонов (узкий экран).
  // Зачем: на планшетах и десктопах (даже с touch) должны быть кнопки зума.
  const [isPhoneViewport, setIsPhoneViewport] = useState(false);
  // Комментарий на русском: защита от дубля (pointerup + click), и fallback для десктопа если pointer события не сработали.
  const photoTapHandledRef = useRef(false);
  // Комментарий на русском: long press для фото (открытие контекстного меню вместо просмотра).
  const photoPressTRef = useRef<number | null>(null);
  const photoPressActiveRef = useRef(false);
  const photoPressStartRef = useRef<{ x: number; y: number } | null>(null);
  const photoLongPressTriggeredRef = useRef(false);

  useEffect(() => {
    // Комментарий на русском: определяем “телефонный” viewport (до sm).
    if (typeof window === 'undefined') return;
    const check = () => {
      const isPhone = window.matchMedia('(max-width: 639px)').matches;
      setIsPhoneViewport(isPhone);
    };
    check();
    const mq = window.matchMedia('(max-width: 639px)');
    if (mq?.addEventListener) mq.addEventListener('change', check);
    return () => {
      if (mq?.removeEventListener) mq.removeEventListener('change', check);
    };
  }, []);

  const closePhotoViewer = useCallback(() => {
    photoViewerOpenRef.current = false;
    setPhotoViewerOpen(false);
    setPhotoViewerDragging(false);
    photoViewerDragYRef.current = 0;
    setPhotoViewerDragY(0);
    setPhotoViewerScale(1);
    setPhotoViewerTranslate({ x: 0, y: 0 });
    photoViewerPinchRef.current = null;
  }, []);

  const clearPhotoPressT = useCallback(() => {
    if (photoPressTRef.current != null) {
      window.clearTimeout(photoPressTRef.current);
      photoPressTRef.current = null;
    }
  }, []);

  const handlePhotoPointerDown = useCallback((e: React.PointerEvent) => {
    if ((e as any)?.button === 2) return; // ПКМ — контекстное меню отдельно
    const isTouchLike =
      e.pointerType === 'touch' ||
      e.pointerType === 'pen' ||
      (typeof navigator !== 'undefined' && Number((navigator as any)?.maxTouchPoints || 0) > 0);
    if (!isTouchLike) return; // Комментарий на русском: long press нужен только на touch.
    photoPressActiveRef.current = true;
    photoLongPressTriggeredRef.current = false;
    photoPressStartRef.current = { x: e.clientX, y: e.clientY };
    clearPhotoPressT();
    photoPressTRef.current = window.setTimeout(() => {
      photoPressTRef.current = null;
      if (!photoPressActiveRef.current) return;
      photoLongPressTriggeredRef.current = true;
      haptic(10);
      setShowMenu(true);
    }, 420);
  }, [clearPhotoPressT]);

  const handlePhotoPointerMove = useCallback((e: React.PointerEvent) => {
    if (!photoPressActiveRef.current) return;
    const st = photoPressStartRef.current;
    if (!st) return;
    const dx = e.clientX - st.x;
    const dy = e.clientY - st.y;
    if (Math.abs(dx) > 8 || Math.abs(dy) > 8) {
      clearPhotoPressT();
      photoPressActiveRef.current = false;
      photoLongPressTriggeredRef.current = false;
      photoPressStartRef.current = null; // Комментарий на русском: это уже не “тап”, скорее скролл.
    }
  }, [clearPhotoPressT]);

  const handlePhotoPointerUp = useCallback((e: React.PointerEvent) => {
    const wasLongPress = photoLongPressTriggeredRef.current;
    const isTouchLike =
      e.pointerType === 'touch' ||
      e.pointerType === 'pen' ||
      (typeof navigator !== 'undefined' && Number((navigator as any)?.maxTouchPoints || 0) > 0);
    const wasTap = !isTouchLike || !!photoPressStartRef.current;
    // Комментарий на русском: если после pointerup прилетит click — игнорируем его.
    photoTapHandledRef.current = true;
    window.setTimeout(() => {
      photoTapHandledRef.current = false;
    }, 0);
    clearPhotoPressT();
    photoPressActiveRef.current = false;
    photoLongPressTriggeredRef.current = false;
    photoPressStartRef.current = null;
    if (wasLongPress) {
      // Комментарий на русском: long press открыл меню — не открываем просмотр фото.
      return;
    }
    if (!wasTap) return;
    // Комментарий на русском: если родитель дал внешний viewer — используем его (с пролистыванием по чату).
    if (onOpenPhotoViewer && photoUrl) {
      onOpenPhotoViewer(photoUrl, Number(message?.id || 0) || 0);
      return;
    }
    // Комментарий на русском: обычный тап — открываем просмотр фото.
    photoViewerOpenRef.current = true;
    setPhotoViewerOpen(true);
    setPhotoViewerDragging(false);
    photoViewerDragYRef.current = 0;
    setPhotoViewerDragY(0);
  }, [clearPhotoPressT, onOpenPhotoViewer, photoUrl, message?.id]);

  const photoDownloadName = useMemo(() => {
    try {
      const a = (attachments || []).find((x: any) => {
        const kind = String(x?.kind || '').toLowerCase();
        return kind === 'photo' || kind === 'image';
      }) as any;
      const name = String(a?.name || a?.original_name || '').trim();
      if (name) return name;
      const mime = String(a?.mime || '').toLowerCase();
      const ext =
        mime === 'image/png' ? 'png' : mime === 'image/webp' ? 'webp' : mime === 'image/gif' ? 'gif' : 'jpg';
      return `photo_${message.id}.${ext}`;
    } catch {
      return `photo_${message.id}.jpg`;
    }
  }, [attachments, message.id]);

  // Комментарий на русском: скачиваем исходный файл с сервера (photoUrl), а не blob из просмотра.
  const downloadPhoto = useCallback(() => {
    // Комментарий на русском: приоритет — оригинальный URL с сервера.
    const u = String(photoUrl || photoSrc || localPhotoSrc || '').trim();
    if (!u || typeof document === 'undefined') return;
    try {
      const a = document.createElement('a');
      a.href = u;
      a.download = photoDownloadName;
      a.rel = 'noopener noreferrer';
      a.target = '_blank';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch {
      // ignore
    }
  }, [localPhotoSrc, photoDownloadName, photoSrc, photoUrl]);

  // Комментарий на русском: функции зума для десктопа.
  const zoomIn = useCallback(() => {
    setPhotoViewerScale((s) => Math.min(4, s + 0.5));
  }, []);
  const zoomOut = useCallback(() => {
    setPhotoViewerScale((s) => {
      const next = Math.max(1, s - 0.5);
      if (next <= 1) setPhotoViewerTranslate({ x: 0, y: 0 });
      return next;
    });
  }, []);
  const zoomReset = useCallback(() => {
    setPhotoViewerScale(1);
    setPhotoViewerTranslate({ x: 0, y: 0 });
  }, []);

  const [fileDownloading, setFileDownloading] = useState(false);
  const [docViewerOpen, setDocViewerOpen] = useState(false);
  const [docViewerKind, setDocViewerKind] = useState<'pdf' | 'docx' | 'xlsx' | 'unknown'>('unknown');
  const [docViewerHtml, setDocViewerHtml] = useState<string>('');
  const [docViewerTable, setDocViewerTable] = useState<string[][]>([]);
  const [docViewerObjUrl, setDocViewerObjUrl] = useState<string>('');
  const [docViewerScale, setDocViewerScale] = useState(1);
  const [docViewerTranslate, setDocViewerTranslate] = useState({ x: 0, y: 0 });
  const [docViewerPdfPage, setDocViewerPdfPage] = useState(1);
  const [docViewerPdfPages, setDocViewerPdfPages] = useState(0);
  const [docViewerPdfLoading, setDocViewerPdfLoading] = useState(false);
  const docViewerPdfRef = useRef<any>(null);
  const docViewerPdfCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const docViewerPdfRenderRef = useRef<any>(null);
  const [docViewerDocPage, setDocViewerDocPage] = useState(1);
  const [docViewerDocPages, setDocViewerDocPages] = useState(1);
  const docViewerDocScrollRef = useRef<HTMLDivElement | null>(null);
  const docViewerDocPageHRef = useRef<number>(0);
  const docViewerDocRafRef = useRef<number | null>(null);
  const docViewerPointerIdRef = useRef<number | null>(null);
  const docViewerStartRef = useRef<{ x: number; y: number } | null>(null);
  const docViewerDraggingRef = useRef(false);
  const docViewerTouchIdRef = useRef<number | null>(null);
  const docViewerTouchStartRef = useRef<{ x: number; y: number } | null>(null);
  const docViewerPinchRef = useRef<{
    distance: number;
    point: { x: number; y: number };
    initialScale: number;
    initialTranslate: { x: number; y: number };
  } | null>(null);
  const fileDownloadName = useMemo(() => {
    const n = String(fileAttRaw?.name || '').trim();
    if (n) return n;
    const id = Number((message as any)?.id || 0) || 0;
    return id ? `file_${id}` : 'file';
  }, [fileAttRaw?.name, message]);
  const fileExt = useMemo(() => {
    const n = String(fileDownloadName || '').toLowerCase().trim();
    const dot = n.lastIndexOf('.');
    if (dot < 0) return '';
    const ext = n.slice(dot + 1).replace(/[^a-z0-9]+/g, '');
    return ext;
  }, [fileDownloadName]);
  const fileBadge = useMemo(() => {
    const ext = fileExt;
    const mime = String(fileAttRaw?.mime || '').toLowerCase();
    const pick = (k: string) => {
      switch (k) {
        case 'pdf':
          return { label: 'PDF', cls: 'bg-site-danger/25 border-site-danger/25 text-site-danger' };
        case 'doc':
        case 'docx':
          return { label: 'W', cls: 'bg-site-accent/25 border-site-accent/25 text-site-accent' };
        case 'xls':
        case 'xlsx':
          return { label: 'X', cls: 'bg-site-success/25 border-site-success/25 text-site-success' };
        case 'zip':
          return { label: 'ZIP', cls: 'bg-site-warning/25 border-site-warning/25 text-site-warning' };
        case 'rar':
          return { label: 'RAR', cls: 'bg-site-accent-secondary/25 border-site-accent-secondary/25 text-site-accent-secondary' };
        case '7z':
          return { label: '7Z', cls: 'bg-site-accent-secondary/25 border-site-accent-secondary/25 text-site-accent-secondary' };
        default:
          return { label: 'FILE', cls: 'bg-site-raised/25 border-site-border text-site-text/85' };
      }
    };
    if (ext) return pick(ext);
    if (mime.includes('pdf')) return pick('pdf');
    if (mime.includes('word') || mime.includes('officedocument.wordprocessingml')) return pick('docx');
    if (mime.includes('excel') || mime.includes('officedocument.spreadsheetml')) return pick('xlsx');
    if (mime.includes('zip')) return pick('zip');
    if (mime.includes('rar')) return pick('rar');
    if (mime.includes('7z')) return pick('7z');
    return pick('');
  }, [fileAttRaw?.mime, fileExt]);
  const formatBytes = useCallback((n: number) => {
    const v = Number(n || 0) || 0;
    if (v <= 0) return '—';
    if (v < 1024) return `${v} B`;
    const kb = v / 1024;
    if (kb < 1024) return `${kb.toFixed(kb < 10 ? 1 : 0)} KB`;
    const mb = kb / 1024;
    if (mb < 1024) return `${mb.toFixed(mb < 10 ? 1 : 0)} MB`;
    const gb = mb / 1024;
    return `${gb.toFixed(gb < 10 ? 1 : 0)} GB`;
  }, []);
  const shouldShowFileCaption = useMemo(() => {
    const t = String(rawText || '').trim();
    if (!t) return false;
    const name = String(fileDownloadName || '').trim();
    if (!name) return true;
    if (t === name) return false;
    if (t.toLowerCase() === 'файл') return false;
    return true;
  }, [fileDownloadName, rawText]);
  const downloadFile = useCallback(async () => {
    const src = String(fileUrl || '').trim();
    if (!src || typeof document === 'undefined') return;
    if (fileDownloading) return;
    setFileDownloading(true);
    try {
      const token = getAccessToken() || '';
      const headers: Record<string, string> = { Accept: '*/*' };
      if (token) headers.Authorization = `Bearer ${token}`;
      const res = await fetch(src, { method: 'GET', credentials: 'include', headers });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const u = URL.createObjectURL(blob);
      try {
        const a = document.createElement('a');
        a.href = u;
        a.download = fileDownloadName;
        a.rel = 'noopener noreferrer';
        document.body.appendChild(a);
        a.click();
        a.remove();
      } finally {
        try { URL.revokeObjectURL(u); } catch {}
      }
    } catch (e: any) {
      try { logNetworkError('Chat file download failed', { url: fileUrl, name: fileDownloadName }, e); } catch {}
    } finally {
      setFileDownloading(false);
    }
  }, [fileDownloadName, fileDownloading, fileUrl]);

  const openFile = useCallback(async () => {
    const src = String(fileUrl || '').trim();
    if (!src || typeof window === 'undefined') return;
    if (fileDownloading) return;
    setFileDownloading(true);
    try {
      const token = getAccessToken() || '';
      const headers: Record<string, string> = { Accept: '*/*' };
      if (token) headers.Authorization = `Bearer ${token}`;
      const res = await fetch(src, { method: 'GET', credentials: 'include', headers });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const u = URL.createObjectURL(blob);
      // Комментарий на русском: открываем в новой вкладке — дальше браузер/Windows предложит программу.
      try {
        window.open(u, '_blank', 'noopener,noreferrer');
      } catch {}
      // Комментарий на русском: не ревокаем сразу — иначе вкладка может не успеть загрузить.
      window.setTimeout(() => {
        try { URL.revokeObjectURL(u); } catch {}
      }, 5 * 60 * 1000);
    } catch (e: any) {
      try { logNetworkError('Chat file open failed', { url: fileUrl, name: fileDownloadName }, e); } catch {}
    } finally {
      setFileDownloading(false);
    }
  }, [fileDownloadName, fileDownloading, fileUrl]);

  const closeDocViewer = useCallback(() => {
    setDocViewerOpen(false);
    setDocViewerKind('unknown');
    setDocViewerHtml('');
    setDocViewerTable([]);
    setDocViewerScale(1);
    setDocViewerTranslate({ x: 0, y: 0 });
    setDocViewerPdfPage(1);
    setDocViewerPdfPages(0);
    setDocViewerPdfLoading(false);
    setDocViewerDocPage(1);
    setDocViewerDocPages(1);
    docViewerDocPageHRef.current = 0;
    if (docViewerDocRafRef.current != null) cancelAnimationFrame(docViewerDocRafRef.current);
    docViewerDocRafRef.current = null;
    try {
      docViewerPdfRenderRef.current?.cancel?.();
    } catch {}
    docViewerPdfRenderRef.current = null;
    try {
      docViewerPdfRef.current?.destroy?.();
    } catch {}
    docViewerPdfRef.current = null;
    docViewerPointerIdRef.current = null;
    docViewerStartRef.current = null;
    docViewerDraggingRef.current = false;
    try {
      if (docViewerObjUrl && docViewerObjUrl.startsWith('blob:')) URL.revokeObjectURL(docViewerObjUrl);
    } catch {}
    setDocViewerObjUrl('');
  }, [docViewerObjUrl]);

  const openDocViewer = useCallback(async () => {
    const src = String(fileUrl || '').trim();
    if (!src || typeof window === 'undefined') return;
    if (fileDownloading) return;

    const ext = String(fileExt || '').toLowerCase();
    const mime = String(fileAttRaw?.mime || '').toLowerCase();
    const isPdf = ext === 'pdf' || mime.includes('pdf');
    const isDocx = ext === 'docx' || mime.includes('officedocument.wordprocessingml');
    const isXlsx = ext === 'xlsx' || mime.includes('officedocument.spreadsheetml');

    setFileDownloading(true);
    setDocViewerOpen(true);
    setDocViewerScale(1);
    setDocViewerTranslate({ x: 0, y: 0 });
    setDocViewerHtml('');
    setDocViewerTable([]);
    setDocViewerPdfPage(1);
    setDocViewerPdfPages(0);
    setDocViewerPdfLoading(false);
    try {
      if (docViewerObjUrl && docViewerObjUrl.startsWith('blob:')) URL.revokeObjectURL(docViewerObjUrl);
    } catch {}
    setDocViewerObjUrl('');

    try {
      const token = getAccessToken() || '';
      const headers: Record<string, string> = { Accept: '*/*' };
      if (token) headers.Authorization = `Bearer ${token}`;
      const res = await fetch(src, { method: 'GET', credentials: 'include', headers });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();

      if (isPdf) {
        setDocViewerKind('pdf');
        setDocViewerPdfLoading(true);
        const ab = await blob.arrayBuffer();
        // Комментарий на русском: грузим pdfjs только в браузере (чтобы сборка на сервере не падала).
        const mod: any = await import('pdfjs-dist/legacy/build/pdf.mjs');
        const pdf: any = mod?.default || mod;
        try {
          const ws = pdf?.GlobalWorkerOptions;
          if (ws && !ws.workerSrc) {
            ws.workerSrc = new URL('pdfjs-dist/legacy/build/pdf.worker.min.mjs', import.meta.url).toString();
          }
        } catch {}
        const task = pdf.getDocument({ data: ab });
        const doc = await task.promise;
        docViewerPdfRef.current = doc;
        setDocViewerPdfPages(Number(doc?.numPages || 0) || 0);
        setDocViewerPdfPage(1);
        setDocViewerPdfLoading(false);
        return;
      }
      if (isDocx) {
        setDocViewerKind('docx');
        const ab = await blob.arrayBuffer();
        const r = await mammoth.convertToHtml({ arrayBuffer: ab as any });
        setDocViewerHtml(String(r?.value || ''));
        return;
      }
      if (isXlsx) {
        setDocViewerKind('xlsx');
        const ab = await blob.arrayBuffer();
        setDocViewerTable(await readXlsxPreviewInWorker(ab));
        return;
      }

      // Комментарий на русском: неизвестный формат — пробуем открыть системным просмотром.
      setDocViewerKind('unknown');
      const u = URL.createObjectURL(blob);
      setDocViewerObjUrl(u);
    } catch (e: any) {
      try { logNetworkError('Chat doc viewer failed', { url: fileUrl, name: fileDownloadName }, e); } catch {}
      closeDocViewer();
    } finally {
      setFileDownloading(false);
    }
  }, [closeDocViewer, docViewerObjUrl, fileAttRaw?.mime, fileDownloadName, fileDownloading, fileExt, fileUrl]);

  useEffect(() => {
    // Комментарий на русском: перерисовываем PDF страницу при смене масштаба/страницы.
    if (!docViewerOpen) return;
    if (docViewerKind !== 'pdf') return;
    const doc = docViewerPdfRef.current;
    const canvas = docViewerPdfCanvasRef.current;
    if (!doc || !canvas) return;
    const pageNo = Math.max(1, Math.min(Number(docViewerPdfPage || 1) || 1, Number(docViewerPdfPages || 0) || 1));
    let cancelled = false;
    void (async () => {
      try {
        setDocViewerPdfLoading(true);
        const page = await doc.getPage(pageNo);
        if (cancelled) return;
        const viewport = page.getViewport({ scale: Math.max(1, Math.min(4, Number(docViewerScale || 1) || 1)) });
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        canvas.width = Math.max(1, Math.floor(viewport.width));
        canvas.height = Math.max(1, Math.floor(viewport.height));
        try {
          docViewerPdfRenderRef.current?.cancel?.();
        } catch {}
        const r = page.render({ canvasContext: ctx, viewport });
        docViewerPdfRenderRef.current = r;
        await r.promise;
      } catch {
        // ignore
      } finally {
        if (!cancelled) setDocViewerPdfLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [docViewerKind, docViewerOpen, docViewerPdfPage, docViewerPdfPages, docViewerScale]);

  useEffect(() => {
    // Комментарий на русском: считаем "страницы" для DOCX/XLSX как экраны скролла (для навигации).
    if (!docViewerOpen) return;
    if (docViewerKind !== 'docx' && docViewerKind !== 'xlsx') return;
    const el = docViewerDocScrollRef.current;
    if (!el) return;

    let cancelled = false;
    const measure = () => {
      if (cancelled) return;
      const h = Math.max(1, Math.floor(el.clientHeight || 0));
      const sh = Math.max(1, Math.floor(el.scrollHeight || 0));
      docViewerDocPageHRef.current = h;
      const pages = Math.max(1, Math.ceil(sh / h));
      setDocViewerDocPages(pages);
      setDocViewerDocPage((p) => Math.max(1, Math.min(pages, Number(p || 1) || 1)));
    };

    // Комментарий на русском: после вставки HTML layout может появиться не сразу.
    const t = window.setTimeout(measure, 0);
    const t2 = window.setTimeout(measure, 120);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
      window.clearTimeout(t2);
    };
  }, [docViewerHtml, docViewerKind, docViewerOpen, docViewerTable]);

  const scrollDocToPage = useCallback((nextPage: number) => {
    const el = docViewerDocScrollRef.current;
    if (!el) return;
    const pages = Math.max(1, Number(docViewerDocPages || 1) || 1);
    const p = Math.max(1, Math.min(pages, Number(nextPage || 1) || 1));
    const h = Math.max(1, Number(docViewerDocPageHRef.current || el.clientHeight || 1) || 1);
    // Комментарий на русском: если пользователь в зуме — сначала выходим в 1x, иначе скролл может не работать.
    if (docViewerScale > 1) {
      setDocViewerScale(1);
      setDocViewerTranslate({ x: 0, y: 0 });
      window.setTimeout(() => {
        try { el.scrollTo({ top: (p - 1) * h, behavior: 'smooth' }); } catch { el.scrollTop = (p - 1) * h; }
        setDocViewerDocPage(p);
      }, 0);
      return;
    }
    try {
      el.scrollTo({ top: (p - 1) * h, behavior: 'smooth' });
    } catch {
      el.scrollTop = (p - 1) * h;
    }
    setDocViewerDocPage(p);
  }, [docViewerDocPages, docViewerScale]);

  useEffect(() => {
    photoViewerOpenRef.current = photoViewerOpen;
    if (!photoViewerOpen) return;

    // Комментарий на русском: блокируем прокрутку под модалкой.
    const prevOverflow = typeof document !== 'undefined' ? document.body.style.overflow : '';
    try {
      if (typeof document !== 'undefined') document.body.style.overflow = 'hidden';
    } catch {}

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closePhotoViewer();
    };
    try {
      window.addEventListener('keydown', onKeyDown);
    } catch {}

    return () => {
      try {
        window.removeEventListener('keydown', onKeyDown);
      } catch {}
      try {
        if (typeof document !== 'undefined') document.body.style.overflow = prevOverflow;
      } catch {}
    };
  }, [closePhotoViewer, photoViewerOpen]);

  useEffect(() => {
    // Комментарий на русском: слушаем online/offline только для фото-сообщений.
    if (!isPhoto) return;
    return _subscribeNetTick(() => setNetTickPhoto(_netTick));
  }, [isPhoto]);

  useEffect(() => {
    if (!isPhoto) {
      setIsPhotoLoading(false);
      setPhotoSrc('');
      setPhotoNeedsDownload(false);
      photoKeyRef.current = '';
      return;
    }
    const src = String(photoUrl || '').trim();
    if (!src) return;

    // Комментарий на русском: blob/data — показываем сразу (optimistic/локально).
    if (src.startsWith('blob:') || src.startsWith('data:')) {
      setIsPhotoLoading(false);
      setPhotoSrc(src);
      setPhotoNeedsDownload(false);
      photoKeyRef.current = src;
      return;
    }

    // Комментарий на русском: если для этого src уже есть objectURL — не грузим повторно.
    if (photoKeyRef.current === src && photoSrc && photoSrc.startsWith('blob:')) return;

    let cancelled = false;
    setIsPhotoLoading(true);
    setPhotoNeedsDownload(false);
    void (async () => {
      try {
        // Комментарий на русском: сначала пробуем постоянный IndexedDB кэш.
        const cached = await getCachedPhoto(src);
        if (cached && !cancelled) {
          const u0 = URL.createObjectURL(cached);
          try {
            const prev = photoObjUrlRef.current;
            if (prev && prev.startsWith('blob:')) URL.revokeObjectURL(prev);
          } catch {}
          photoObjUrlRef.current = u0;
          photoKeyRef.current = src;
          setPhotoSrc(u0);
          setPhotoNeedsDownload(false);
          return;
        }
        // Комментарий на русском: если кэша нет — автоматически скачиваем и показываем placeholder (как Telegram).
        if (typeof navigator !== 'undefined' && navigator.onLine === false) {
          setPhotoNeedsDownload(true);
          return;
        }
        const token = getAccessToken() || '';
        const headers: Record<string, string> = { Accept: 'image/*,*/*;q=0.8' };
        if (token) headers.Authorization = `Bearer ${token}`;
        const res = await fetch(src, { method: 'GET', credentials: 'include', headers });
        if (!res.ok) {
          setPhotoNeedsDownload(true);
          return;
        }
        const ct = String(res.headers.get('content-type') || '').toLowerCase();
        if (!ct.startsWith('image/')) {
          setPhotoNeedsDownload(true);
          return;
        }
        const blob = await res.blob();
        // Комментарий на русском: сохраняем в постоянный IndexedDB кэш с привязкой к чату.
        void putPhotoToCache(src, blob, chatId, ct);
        if (cancelled) return;
        const u = URL.createObjectURL(blob);
        try {
          const prev = photoObjUrlRef.current;
          if (prev && prev.startsWith('blob:')) URL.revokeObjectURL(prev);
        } catch {}
        photoObjUrlRef.current = u;
        photoKeyRef.current = src;
        setPhotoSrc(u);
        setPhotoNeedsDownload(false);
      } catch {
        setPhotoNeedsDownload(true);
      } finally {
        if (!cancelled) setIsPhotoLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isPhoto, photoSrc, photoUrl, chatId, netTickPhoto]);

  const downloadPhotoManual = useCallback(async () => {
    const src = String(photoUrl || '').trim();
    if (!src) return;
    if (isPhotoLoading) return;
    setIsPhotoLoading(true);
    setPhotoNeedsDownload(false);
    try {
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        setPhotoNeedsDownload(true);
        return;
      }
      const token = getAccessToken() || '';
      const headers: Record<string, string> = { Accept: 'image/*,*/*;q=0.8' };
      if (token) headers.Authorization = `Bearer ${token}`;
      const res = await fetch(src, { method: 'GET', credentials: 'include', headers });
      if (!res.ok) {
        setPhotoNeedsDownload(true);
        return;
      }
      const ct = String(res.headers.get('content-type') || '').toLowerCase();
      if (!ct.startsWith('image/')) {
        setPhotoNeedsDownload(true);
        return;
      }
      const blob = await res.blob();
      // Комментарий на русском: сохраняем в постоянный IndexedDB кэш с привязкой к чату.
      void putPhotoToCache(src, blob, chatId, ct);
      const u = URL.createObjectURL(blob);
      try {
        const prev = photoObjUrlRef.current;
        if (prev && prev.startsWith('blob:')) URL.revokeObjectURL(prev);
      } catch {}
      photoObjUrlRef.current = u;
      photoKeyRef.current = src;
      setPhotoSrc(u);
      setPhotoNeedsDownload(false);
    } catch {
      setPhotoNeedsDownload(true);
    } finally {
      setIsPhotoLoading(false);
    }
  }, [photoUrl, isPhotoLoading, chatId]);

  useEffect(() => {
    // Комментарий на русском: освобождаем blob URL фото на unmount.
    return () => {
      try {
        if (photoObjUrlRef.current && photoObjUrlRef.current.startsWith('blob:')) URL.revokeObjectURL(photoObjUrlRef.current);
      } catch {}
      photoObjUrlRef.current = '';
    };
  }, []);

  // Комментарий на русском: редактировать можно только свои текстовые сообщения.
  const canEditAction = !!onEdit && isOwn && !isVoice;
  // Комментарий на русском: закрепление/открепление управляется родителем (если колбэк есть — показываем).
  const canPinAction = !!onPin;
  const fallbackVoiceSrc = typeof anyMsg?.local_voice_src === 'string' ? String(anyMsg.local_voice_src) : '';
  // Комментарий на русском: voiceSrc должен быть реальным URL или data:/blob:, а не "Голосовое сообщение".
  const isEffectiveUrl =
    effective.startsWith('/') ||
    effective.startsWith('blob:') ||
    effective.startsWith('data:') ||
    /^https?:\/\//.test(effective);
  const voiceSrc = isRealtimeVoice
    ? `data:${rawText.slice('__voice__:'.length)}`
    : isEffectiveUrl
      ? effective.startsWith('/')
        ? resolveVoiceUrl(effective)
        : effective
      : '';
  // Комментарий на русском: при инициализации предпочитаем fallback blob URL — он гарантированно работает.
  // Зачем: для свежеотправленных голосовых серверный файл может быть ещё не готов.
  const [audioSrc, setAudioSrc] = useState<string>(fallbackVoiceSrc || voiceSrc);
  const loadTRef = useRef<number | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isVoiceLoading, setIsVoiceLoading] = useState(false);
  // Комментарий на русском: прогресс загрузки файла (0-100), как в Telegram.
  const [downloadProgress, setDownloadProgress] = useState<number | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [dur, setDur] = useState(0);
  const [pos, setPos] = useState(0);
  const [rate, setRate] = useState<1 | 1.5 | 2>(1);
  const [cachedVoiceSrc, setCachedVoiceSrc] = useState<string>('');
  // Комментарий на русском: прямое воспроизведение по server URL (fallback, когда CacheStorage/fetch упирается в CORS).
  const [directVoiceSrc, setDirectVoiceSrc] = useState<string>('');
  const cachedVoiceUrlRef = useRef<string>('');
  // Комментарий на русском: какой voiceSrc соответствует текущему cachedVoiceUrlRef (нужно, чтобы не путать файлы и безопасно чистить память).
  const cachedVoiceKeyRef = useRef<string>('');
  const calcInProgressRef = useRef(false);
  // Комментарий на русском: защита от параллельных загрузок одного и того же файла.
  const downloadUrlRef = useRef<string>('');
  const downloadAbortRef = useRef<AbortController | null>(null);
  // Комментарий на русском: если пользователь нажал Play во время загрузки — автозапуск после подготовки.
  const autoPlayAfterPrepareRef = useRef(false);
  // Комментарий на русском: флаг для отслеживания намеренного воспроизведения пользователем (предотвращает автоплей при ошибках загрузки на десктопе).
  const userInitiatedPlayRef = useRef(false);
  const rafRef = useRef<number | null>(null);
  const waveRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef(false);
  const durRef = useRef(0);
  const basePerfRef = useRef(0);
  const baseTimeRef = useRef(0);
  const lastCtRef = useRef(-1);
  // Комментарий на русском: “последнее UI-время” для прогресса (анти-дёргание iOS).
  // Зачем: iOS Safari в начале может кратко откатывать currentTime/readyState, из-за чего прогресс прыгает назад.
  const lastUiTimeRef = useRef(-2);
  const lastSecRef = useRef(-2);
  const waveDecodeAllowedRef = useRef(false);
  const [waveBarsReal, setWaveBarsReal] = useState<number[]>([]);
  const [linkPreview, setLinkPreview] = useState<LinkPreview | null>(null);
  const [linkPreviewLoading, setLinkPreviewLoading] = useState(false);
  const linkPreviewAbortRef = useRef<AbortController | null>(null);
  const [linkPreviewImageSrc, setLinkPreviewImageSrc] = useState<string | null>(null);
  const linkPreviewObjectUrlRef = useRef<string>('');
  // Комментарий на русском: refs к DOM-элементам столбиков волны (фон + “прогресс”).
  // Зачем: подсветку текущего положения делаем без React setState, чтобы не было ререндер-штормов на iOS.
  const waveBarsBgRef = useRef<Array<HTMLSpanElement | null>>([]);
  const waveBarsFgRef = useRef<Array<HTMLSpanElement | null>>([]);
  const waveHeadIdxRef = useRef<number>(-1);
  const clearLoadT = () => {
    if (loadTRef.current != null) {
      window.clearTimeout(loadTRef.current);
      loadTRef.current = null;
    }
  };
  useEffect(() => {
    // Комментарий на русском: при смене src (blob -> server URL) принудительно обновляем audio,
    // иначе iOS Safari иногда “залипает” на бесконечной загрузке.
    if (!isVoice) return;
    clearLoadT();
    setAudioSrc((prev) => {
      // Комментарий на русском: свежая отправка = есть local blob fallback.
      // Зачем: серверный файл может появляться с задержкой/недописанным (получается “старт и сразу в 0 без звука”).
      if (fallbackVoiceSrc && prev === fallbackVoiceSrc && voiceSrc !== fallbackVoiceSrc) return prev;
      return voiceSrc;
    });
    // Комментарий на русском: прямой server URL привязываем к текущему voiceSrc — при смене сообщения сбрасываем.
    setDirectVoiceSrc('');
  }, [fallbackVoiceSrc, isVoice, voiceSrc]);

  const formatT = (sec: number) => {
    const s = Math.max(0, Math.floor(sec || 0));
    const m = Math.floor(s / 60);
    return `${m}:${String(s % 60).padStart(2, '0')}`;
  };

  const applyPct = useCallback((pct: number) => {
    const el = waveRef.current;
    if (!el) return;
    const pctClamped = Math.max(0.5, Math.min(99.5, pct));
    const v = `${pctClamped}%`;
    el.style.setProperty('--voice-pct', v);

    // Комментарий на русском: вместо белой “линии” подсвечиваем текущий столбик (зелёный + чуть выше).
    const idx = Math.max(0, Math.min(WAVE_BARS_COUNT - 1, Math.floor((pctClamped / 100) * WAVE_BARS_COUNT)));
    const prev = waveHeadIdxRef.current;
    if (prev === idx) return;
    waveHeadIdxRef.current = idx;
    const resetBar = (b: HTMLSpanElement | null) => {
      if (!b) return;
      try {
        b.style.removeProperty('background-color');
        b.style.removeProperty('transform');
      } catch {}
    };
    const paintBar = (b: HTMLSpanElement | null, color: string, alpha: number, scaleY: number) => {
      if (!b) return;
      try {
        b.style.backgroundColor = `rgba(${color}, ${alpha})`;
        b.style.transform = `scaleY(${scaleY})`;
      } catch {}
    };

    // Комментарий на русском: подсвечиваем 3 столбика (левый/центр/правый).
    // Центр — темнее и выше (+25%), соседи — светлее и чуть ниже.
    const clampI = (i: number) => Math.max(0, Math.min(WAVE_BARS_COUNT - 1, i));
    const oldIdxs = [clampI(prev - 1), clampI(prev), clampI(prev + 1)];
    const newIdxs = [clampI(idx - 1), clampI(idx), clampI(idx + 1)];

    const resetIdx = (i: number) => {
      resetBar(waveBarsBgRef.current[i] || null);
      resetBar(waveBarsFgRef.current[i] || null);
    };
    // Сбрасываем старые, которые больше не актуальны
    for (const i of oldIdxs) {
      if (!newIdxs.includes(i)) resetIdx(i);
    }

    const GREEN_SIDE = '10, 161, 90'; // green-500
    const GREEN_CENTER = '5, 215, 216'; // green-700
    const SCALE_CENTER = 1.25; // +25%
    const SCALE_SIDE = 1.14; // чуть ниже, чем центр

    const paintIdx = (i: number, isCenter: boolean) => {
      const color = isCenter ? GREEN_CENTER : GREEN_SIDE;
      const scale = isCenter ? SCALE_CENTER : SCALE_SIDE;
      // Фон (серый слой) делаем слабее, прогресс-слой — ярче.
      paintBar(waveBarsBgRef.current[i] || null, color, isCenter ? 0.55 : 0.35, scale);
      paintBar(waveBarsFgRef.current[i] || null, color, isCenter ? 0.95 : 0.65, scale);
    };
    paintIdx(clampI(idx - 1), false);
    paintIdx(clampI(idx), true);
    paintIdx(clampI(idx + 1), false);
  }, []);

  const applyProgress = useCallback((t: number, d: number) => {
    if (!(d > 0)) {
      applyPct(0.5);
      return;
    }
    applyPct((t / d) * 100);
  }, []);

  const stopRaf = useCallback(() => {
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  const startRaf = useCallback(() => {
    stopRaf();
    const a0 = audioRef.current;
    const now0 = performance.now();
    basePerfRef.current = now0;
    baseTimeRef.current = a0 ? (a0.currentTime || 0) : 0;
    lastCtRef.current = baseTimeRef.current;
    lastUiTimeRef.current = baseTimeRef.current;

    const tick = () => {
      const a = audioRef.current;
      if (!a) return;

      const now = performance.now();
      const ct = a.currentTime || 0;
      const rt = Number.isFinite(a.playbackRate) ? a.playbackRate : 1;

      // Комментарий на русском: duration на iOS может быть не готов сразу.
      // Поэтому берём duration -> seekable.end -> buffered.end -> последнее известное.
      let d = Number.isFinite(a.duration) ? a.duration : durRef.current;
      if (!(d > 0)) {
        try {
          const s = a.seekable;
          if (s && s.length) d = s.end(s.length - 1);
        } catch {}
      }
      if (!(d > 0)) {
        try {
          const b = a.buffered;
          if (b && b.length) d = b.end(b.length - 1);
        } catch {}
      }
      // Комментарий на русском: НЕ обновляем React-state dur в rAF на каждом кадре.
      // Зачем: при медленной загрузке/буферизации значения seekable/buffered могут часто меняться,
      // это вызывает шторм ререндеров (в DevTools выглядит как "зависание" внутри React DOM updateProps).
      // dur/state обновляем через события audio: loadedmetadata/durationchange/canplay (см. эффект ниже).
      if (d > 0) durRef.current = d;

      // Комментарий на русском: если currentTime “прыгает” (seek/обновление Safari) — пересинхронизируем базу.
      if (lastCtRef.current < 0 || Math.abs(ct - lastCtRef.current) > 0.04 || (a as any).seeking) {
        lastCtRef.current = ct;
        baseTimeRef.current = ct;
        basePerfRef.current = now;
      }

      // Комментарий на русском: интерполяция делает ползунок плавным даже если currentTime обновляется редко.
      let est = baseTimeRef.current + ((now - basePerfRef.current) / 1000) * rt;
      // Комментарий на русском: на iOS Safari readyState часто = 2 даже при нормальном проигрывании,
      // поэтому НЕ отключаем интерполяцию на <3 (иначе ползунок двигается рывками ~1 раз/сек).
      // Отключаем только когда реально нет данных для проигрывания.
      if ((a.readyState || 0) < 2) est = ct;

      // Комментарий на русском: защита от микроскопических “откатов” времени (типично на старте iOS Safari).
      // Это лечит визуальный прыжок волны назад в первые секунды, не ломая seek (при seeking — разрешаем любые скачки).
      const isSeeking = !!(a as any).seeking;
      let uiT = est;
      if (!isSeeking && !a.paused && !a.ended) {
        const prevUi = lastUiTimeRef.current;
        if (prevUi >= 0 && uiT < prevUi - 0.12) uiT = prevUi; // 120мс “гистерезис” против дрожания
        else lastUiTimeRef.current = uiT;
      } else {
        lastUiTimeRef.current = uiT;
      }

      if (d > 0) {
        uiT = Math.max(0, Math.min(d, uiT));
        applyProgress(uiT, d);
      } else {
        // Комментарий на русском: если длительность ещё неизвестна (iOS) — НЕ “убегаем” вперёд,
        // иначе при появлении duration будет заметный откат назад. Делаем лёгкую пульсацию в пределах 1-го столбика.
        const t = (now - basePerfRef.current) / 1000;
        const pulse = 0.5 + 0.9 * (0.5 + 0.5 * Math.sin(t * 6)); // 0.5..1.4%
        applyPct(pulse);
      }

      // Комментарий на русском: текст времени обновляем редко (раз в секунду), чтобы не грузить React на iOS.
      const sec = Math.floor(ct);
      if (sec !== lastSecRef.current) {
        lastSecRef.current = sec;
        setPos(ct);
      }

      if (!a.paused && !a.ended) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        if (d > 0) applyProgress(ct, d);
        stopRaf();
      }
    };
    rafRef.current = requestAnimationFrame(tick);
  }, [applyProgress, stopRaf]);

  useEffect(() => {
    // Комментарий на русском: применяем скорость к текущему audio (важно после смены src/key).
    if (!isVoice) return;
    const a = audioRef.current;
    if (!a) return;
    try {
      a.playbackRate = rate;
    } catch {}
    // Если уже играет — пересинхронизируем “плавную” шкалу.
    if (!a.paused && !a.ended) {
      lastCtRef.current = a.currentTime || 0;
      baseTimeRef.current = lastCtRef.current;
      basePerfRef.current = performance.now();
    }
  }, [isVoice, audioSrc, cachedVoiceSrc, rate]);

  useEffect(() => {
    // Комментарий на русском: на unmount гарантированно гасим rAF (чтобы не было setState после ухода).
    return () => stopRaf();
  }, [stopRaf]);

  useEffect(() => {
    // Комментарий на русском: подхватываем локальный кэш голосового как blob: URL.
    // Зачем: проигрывание без сети + не дёргаем сервер повторно.
    if (!isVoice) return;
    const src = String(voiceSrc || '').trim();
    if (!src || src.startsWith('blob:') || src.startsWith('data:')) return;

    // Комментарий на русском: если <audio> уже использует objectURL, НЕ трогаем его.
    // Иначе браузер может отклонить загрузку как "URL safety check" (особенно при смене prefetchVoice).
    const a0 = audioRef.current;
    const prevUrl0 = cachedVoiceUrlRef.current;
    const prevInUse0 = !!a0 && !!prevUrl0 && (a0.src === prevUrl0 || a0.currentSrc === prevUrl0);
    if (prevInUse0) return;

    // Комментарий на русском: iOS PWA часто ломает blob: URL из CacheStorage (errorCode=4).
    // Поэтому в standalone-режиме НЕ создаём objectURL и всегда играем по прямому server URL.
    if (isIOSStandaloneApp()) {
      try {
        if (prevUrl0) URL.revokeObjectURL(prevUrl0);
      } catch {}
      cachedVoiceUrlRef.current = '';
      cachedVoiceKeyRef.current = '';
      setCachedVoiceSrc('');
      return;
    }

    // Комментарий на русском: если voiceSrc поменялся — чистим старый objectURL (но только если он не используется audio).
    if (cachedVoiceKeyRef.current && cachedVoiceKeyRef.current !== src && prevUrl0) {
      try {
        URL.revokeObjectURL(prevUrl0);
      } catch {}
      cachedVoiceUrlRef.current = '';
      cachedVoiceKeyRef.current = '';
      setCachedVoiceSrc('');
    }

    // Комментарий на русском: если для этого src уже есть objectURL — ничего не делаем.
    if (cachedVoiceKeyRef.current === src && cachedVoiceUrlRef.current) return;

    let cancelled = false;
    void (async () => {
      let b0 = await getCachedVoiceBlob(src, voiceMime);
      if (cancelled) return;
      // Комментарий на русском: волну можно считать для 100, но файл скачиваем в CacheStorage только для первых N по rank.
      const r = Number.isFinite(prefetchRank as any) ? (prefetchRank as number) : 9999;
      if (!b0 && prefetchVoice && r <= VOICE_FILE_PREFETCH_RANK_LIMIT) {
        const res = await downloadVoiceToCache(src, undefined, { mimeHint: voiceMime });
        if (cancelled) return;
        b0 = res.blob;
        if (cancelled) return;
      }
      if (!b0) return;
      const a = audioRef.current;
      if (a && !a.paused && !a.ended) return; // не переключаем src во время проигрывания
      const u = URL.createObjectURL(b0);
      // Комментарий на русском: освобождаем старый objectURL ТОЛЬКО если он не используется audio.
      try {
        const prevUrl = cachedVoiceUrlRef.current;
        const prevInUse = !!a && !!prevUrl && (a.src === prevUrl || a.currentSrc === prevUrl);
        if (prevUrl && !prevInUse) URL.revokeObjectURL(prevUrl);
      } catch {}
      cachedVoiceUrlRef.current = u;
      cachedVoiceKeyRef.current = src;
      setCachedVoiceSrc(u);
    })();
    return () => {
      cancelled = true;
    };
  }, [isVoice, prefetchVoice, voiceMime, voiceSrc]);

  useEffect(() => {
    // Комментарий на русском: на unmount гарантированно освобождаем blob: URL кэша.
    return () => {
      try {
        if (cachedVoiceUrlRef.current) URL.revokeObjectURL(cachedVoiceUrlRef.current);
      } catch {}
      cachedVoiceUrlRef.current = '';
    };
  }, []);

  useEffect(() => {
    // Комментарий на русском: на unmount останавливаем аудио и очищаем activeVoiceAudio.
    // Зачем: если пользователь переключился в другой чат, старое голосовое НЕ должно продолжать играть при возврате.
    return () => {
      const a = audioRef.current;
      if (!a) return;
      try {
        if (!a.paused) a.pause();
      } catch {}
      if (activeVoiceAudio === a) activeVoiceAudio = null;
    };
  }, []);

  useEffect(() => {
    // Комментарий на русском: страховка для iOS — duration/metadata могут приходить поздно.
    if (!isVoice) return;
    const a = audioRef.current;
    if (!a) return;

    const upd = () => {
      const d = Number.isFinite(a.duration) ? a.duration : durRef.current;
      if (d > 0 && d !== durRef.current) {
        durRef.current = d;
        setDur(d);
      }
      if (d > 0) applyProgress(a.currentTime || 0, d);
    };

    const onPlay = () => {
      setIsPlaying(true);
      setIsVoiceLoading(true);
      // Комментарий на русском: после первого запуска разрешаем “реальную” волну, но считаем её позже (в idle/после паузы).
      waveDecodeAllowedRef.current = true;
      upd();
      startRaf();
    };
    const onPlaying = () => {
      // Комментарий на русском: как только реально пошло проигрывание — убираем "крутилку загрузки".
      setIsVoiceLoading(false);
      calcInProgressRef.current = false;
    };
    const onWaiting = () => {
      // Комментарий на русском: Telegram-поведение — если буферимся, показываем загрузку.
      if (!a.paused && !a.ended) setIsVoiceLoading(true);
    };
    const onPause = () => {
      setIsPlaying(false);
      if (!calcInProgressRef.current) setIsVoiceLoading(false);
      if (activeVoiceAudio === a) activeVoiceAudio = null;
      userInitiatedPlayRef.current = false;
      upd();
      stopRaf();
    };
    const onEnded = () => {
      setIsPlaying(false);
      if (!calcInProgressRef.current) setIsVoiceLoading(false);
      if (activeVoiceAudio === a) activeVoiceAudio = null;
      userInitiatedPlayRef.current = false;
      upd();
      stopRaf();
    };
    const onErr = () => {
      if (!calcInProgressRef.current) setIsVoiceLoading(false);
    };

    a.addEventListener('loadedmetadata', upd);
    a.addEventListener('durationchange', upd);
    a.addEventListener('canplay', upd);
    a.addEventListener('play', onPlay);
    a.addEventListener('playing', onPlaying);
    a.addEventListener('waiting', onWaiting);
    a.addEventListener('stalled', onWaiting);
    a.addEventListener('error', onErr);
    a.addEventListener('pause', onPause);
    a.addEventListener('ended', onEnded);

    upd();

    return () => {
      a.removeEventListener('loadedmetadata', upd);
      a.removeEventListener('durationchange', upd);
      a.removeEventListener('canplay', upd);
      a.removeEventListener('play', onPlay);
      a.removeEventListener('playing', onPlaying);
      a.removeEventListener('waiting', onWaiting);
      a.removeEventListener('stalled', onWaiting);
      a.removeEventListener('error', onErr);
      a.removeEventListener('pause', onPause);
      a.removeEventListener('ended', onEnded);
    };
  }, [applyProgress, audioSrc, cachedVoiceSrc, isVoice, startRaf, stopRaf]);

  useEffect(() => {
    // Комментарий на русском: строим waveform “похожий на звук” (best-effort).
    // Если браузер/формат не позволяет декодировать — используем псевдо-waveform.
    if (!isVoice) return;
    const src = String(voiceSrc || '');
    if (!src) return;

    const cached = getCachedVoiceWave(src);
    if (cached?.bars?.length === WAVE_BARS_COUNT) {
      setWaveBarsReal(cached.bars);
      if (cached.dur && !(durRef.current > 0)) {
        durRef.current = cached.dur;
        setDur(cached.dur);
      }
      return;
    }

    // Комментарий на русском: iOS PWA — не декодируем аудио в фоне (меньше шансов “убить” звук/запись).
    if (isIOSStandaloneApp()) return;

    const allowCompute = prefetchVoice || waveDecodeAllowedRef.current;
    if (!allowCompute) return;
    if (isPlaying) return;

    let cancelled = false;
    void prefetchVoiceWave(src, { rank: (prefetchRank as number) | 0 }).then((v) => {
      if (cancelled || !v?.bars?.length) return;
      setWaveBarsReal(v.bars);
      if (v.dur && !(durRef.current > 0)) {
        durRef.current = v.dur;
        setDur(v.dur);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [isPlaying, isVoice, prefetchRank, prefetchVoice, voiceSrc]);

  const togglePlay = () => {
    const a = audioRef.current;
    if (!a) return;

    // Комментарий на русском: если идёт подготовка/загрузка — клик по кнопке = отмена (как "стоп" в Telegram).
    if (isDownloading) {
      autoPlayAfterPrepareRef.current = false;
      userInitiatedPlayRef.current = false;
      try {
        downloadAbortRef.current?.abort();
      } catch {}
      downloadAbortRef.current = null;
      downloadUrlRef.current = '';
      setIsDownloading(false);
      setDownloadProgress(null);
      setIsVoiceLoading(false);
      return;
    }

    // Комментарий на русском: если уже играет — ставим на паузу.
    if (!a.paused) {
      a.pause();
      userInitiatedPlayRef.current = false;
      return;
    }

    // Комментарий на русском: пользователь нажал Play — фиксируем намеренное воспроизведение.
    userInitiatedPlayRef.current = true;

    // Комментарий на русском: останавливаем предыдущее голосовое (если играет другое).
    if (activeVoiceAudio && activeVoiceAudio !== a) {
      try {
        activeVoiceAudio.pause();
      } catch {}
    }
    activeVoiceAudio = a;

    // Комментарий на русском: важно для iOS/мобилок — play() должен вызываться СРАЗУ по клику,
    // иначе браузер может заблокировать воспроизведение (потеря user gesture).
    const playNow = (src: string) => {
      const a0 = audioRef.current;
      if (!a0) return;
      if (!src) return;
      try {
        a0.playbackRate = rate;
      } catch {}
      try {
        if (a0.src !== src) {
          a0.src = src;
          a0.load?.();
        }
      } catch {}
      try {
        const p = a0.play();
        if (p && typeof (p as any).catch === 'function') (p as any).catch(() => {});
      } catch {}
    };

    // Комментарий на русском: serverUrl только если это реальный http/https URL (не blob/data и не текст).
    const serverUrl =
      voiceSrc &&
      !voiceSrc.startsWith('blob:') &&
      !voiceSrc.startsWith('data:') &&
      /^https?:\/\//.test(voiceSrc)
        ? voiceSrc
        : '';
    const localBlob =
      (fallbackVoiceSrc && (fallbackVoiceSrc.startsWith('blob:') || fallbackVoiceSrc.startsWith('data:')) ? fallbackVoiceSrc : '') ||
      (cachedVoiceSrc && cachedVoiceSrc.startsWith('blob:') ? cachedVoiceSrc : '') ||
      (audioSrc && (audioSrc.startsWith('blob:') || audioSrc.startsWith('data:')) ? audioSrc : '');

    // 1) Если есть локальный blob — играем сразу из него.
    if (localBlob) {
      setDirectVoiceSrc('');
      playNow(localBlob);
      return;
    }

    // 2) Если локального файла нет — сначала готовим (скачиваем в Cache Storage), показываем загрузку, потом играем ИЗ КЭША.
    if (!serverUrl) {
      // Комментарий на русском: нет ни локального blob, ни серверного URL — проигрывание невозможно.
      logVoiceError('Нет источника для воспроизведения', {
        voiceSrc,
        voiceUrl,
        effective,
        fallbackVoiceSrc,
        audioSrc,
        cachedVoiceSrc,
        messageId: message?.id,
      });
      return;
    }
    if (!isOnline()) {
      logVoiceError('Нет сети для загрузки голосового', { serverUrl, messageId: message?.id });
      return;
    }

    // Комментарий на русском: UX-фикс.
    // Зачем: "сначала скачать в CacheStorage → потом play(blob)" часто выглядит как вечная загрузка,
    // а play() после async может быть заблокирован/нестабилен на мобильных. Поэтому сначала играем напрямую по URL.
    // Комментарий на русском: на iOS первый play() может “съедаться”, если React после клика перерендерит <audio> и заново выставит src.
    // Поэтому синхронно применяем src (через state), и только потом вызываем play().
    if (directVoiceSrc !== serverUrl) {
      flushSync(() => setDirectVoiceSrc(serverUrl));
    }
    playNow(serverUrl);
    return;
  };

  const seekTo = (next: number) => {
    const a = audioRef.current;
    if (!a) return;
    let d = dur || (Number.isFinite(a.duration) ? a.duration : 0);
    if (!(d > 0)) {
      try {
        const s = a.seekable;
        if (s && s.length) d = s.end(s.length - 1);
      } catch {}
    }
    if (!(d > 0)) {
      try {
        const b = a.buffered;
        if (b && b.length) d = b.end(b.length - 1);
      } catch {}
    }
    const t = Math.max(0, Math.min(d || 0, next));
    a.currentTime = t;
    lastCtRef.current = t;
    baseTimeRef.current = t;
    basePerfRef.current = performance.now();
    setPos(t);
    if (d > 0) applyProgress(t, d);
  };

  const seekFromClientX = (clientX: number) => {
    const el = waveRef.current;
    if (!el) return;
    const a = audioRef.current;
    const d = dur || (a && Number.isFinite(a.duration) ? a.duration : 0);
    if (!d) return;
    const r = el.getBoundingClientRect();
    const w = Math.max(1, r.width);
    const x = Math.max(0, Math.min(w, clientX - r.left));
    seekTo((x / w) * d);
  };

  const toggleRate = () => {
    setRate((r) => (r === 1 ? 1.5 : r === 1.5 ? 2 : 1));
  };

  const waveBarsFallback = useMemo(() => {
    // Комментарий на русском: псевдо-waveform (как в Telegram/iMessage), без тяжёлого анализа аудио.
    // Зачем: быстро, стабильно, одинаково на всех устройствах.
    const seedStr = `${message?.id || 0}:${String(voiceSrc || '')}`;
    let h = 2166136261;
    for (let i = 0; i < seedStr.length; i++) {
      h ^= seedStr.charCodeAt(i);
      h = Math.imul(h, 16777619);
    }
    let x = (h >>> 0) || 1;
    const rnd = () => {
      x = (x * 1664525 + 1013904223) >>> 0;
      return x / 4294967296;
    };
    const n = WAVE_BARS_COUNT;
    const out: number[] = [];
    for (let i = 0; i < n; i++) {
      const v = 0.25 + rnd() * 0.75;
      out.push(v);
    }
    return out;
  }, [message?.id, voiceSrc]);

  const waveBars = waveBarsReal.length === WAVE_BARS_COUNT ? waveBarsReal : waveBarsFallback;
  
  const firstUrl = useMemo(() => {
    if (!enableLinkPreview || isVoice || isPhoto) return null;
    if ((message as any)?.link_preview_disabled) return null;
    return extractFirstUrl(rawText);
  }, [enableLinkPreview, isPhoto, isVoice, rawText, (message as any)?.link_preview_disabled]);

  useEffect(() => {
    if (!enableLinkPreview || isVoice) {
      setLinkPreview(null);
      setLinkPreviewLoading(false);
      linkPreviewAbortRef.current?.abort();
      if (linkPreviewObjectUrlRef.current) {
        URL.revokeObjectURL(linkPreviewObjectUrlRef.current);
        linkPreviewObjectUrlRef.current = '';
      }
      setLinkPreviewImageSrc(null);
      return;
    }
    if (!firstUrl) {
      setLinkPreview(null);
      setLinkPreviewLoading(false);
      linkPreviewAbortRef.current?.abort();
      if (linkPreviewObjectUrlRef.current) {
        URL.revokeObjectURL(linkPreviewObjectUrlRef.current);
        linkPreviewObjectUrlRef.current = '';
      }
      setLinkPreviewImageSrc(null);
      return;
    }

    const cached = linkPreviewCache.get(firstUrl);
    if (cached) {
      setLinkPreview(cached);
      setLinkPreviewLoading(false);
      return;
    }

    linkPreviewAbortRef.current?.abort();
    const ac = new AbortController();
    linkPreviewAbortRef.current = ac;
    setLinkPreviewLoading(true);

    const run = async () => {
      try {
        const data = await fetchLinkPreviewClient(firstUrl, ac.signal);
        if (!data?.url) return;
        const normalized: LinkPreview = {
          url: data.url,
          domain: data.domain || firstUrl,
          title: data.title,
          description: data.description,
          image: data.image,
          kind: data.kind === 'video' ? 'video' : 'site',
        };
        linkPreviewCache.set(firstUrl, normalized);
        if (!ac.signal.aborted) setLinkPreview(normalized);
      } catch (err) {
        if (!ac.signal.aborted) {
          logNetworkError('Не удалось получить предпросмотр ссылки', { url: firstUrl, error: String(err) });
        }
      } finally {
        if (!ac.signal.aborted) setLinkPreviewLoading(false);
      }
    };

    void run();
    return () => {
      ac.abort();
    };
  }, [enableLinkPreview, firstUrl, isVoice, rawText]);

  useEffect(() => {
    // Комментарий на русском: сжимаем og:image, если она тяжелее лимита, используя уже готовый компрессор из админки.
    if (!enableLinkPreview || !linkPreview?.image) {
      if (linkPreviewObjectUrlRef.current) {
        URL.revokeObjectURL(linkPreviewObjectUrlRef.current);
        linkPreviewObjectUrlRef.current = '';
      }
      setLinkPreviewImageSrc(null);
      return;
    }
    setLinkPreviewImageSrc(linkPreview.image);

    const ac = new AbortController();
    const abort = () => ac.abort();

    const compress = async () => {
      try {
        const res = await fetch(linkPreview.image, { signal: ac.signal });
        if (!res.ok) return;
        const blob = await res.blob();
        if (ac.signal.aborted) return;
        if (blob.size <= LINK_IMAGE_MAX_BYTES) return;

        const originalName = linkPreview.image.split('/').pop() || 'preview.jpg';
        const file = new File([blob], originalName, { type: blob.type || 'image/jpeg' });
        const compressed = await optimizeImage(file);
        if (ac.signal.aborted) return;
        // Комментарий: если компрессия не уменьшила файл — оставляем оригинал.
        if (compressed.size >= file.size) return;
        const objUrl = URL.createObjectURL(compressed);
        if (linkPreviewObjectUrlRef.current) {
          URL.revokeObjectURL(linkPreviewObjectUrlRef.current);
        }
        linkPreviewObjectUrlRef.current = objUrl;
        setLinkPreviewImageSrc(objUrl);
      } catch {
        // Тихо падаем — показываем оригинал.
      }
    };

    void compress();
    return () => {
      abort();
      if (linkPreviewObjectUrlRef.current) {
        URL.revokeObjectURL(linkPreviewObjectUrlRef.current);
        linkPreviewObjectUrlRef.current = '';
      }
    };
  }, [enableLinkPreview, linkPreview?.image]);

  // Форматируем текст с XSS защитой
  const formattedText = useMemo(
    () => {
      if (isVoice) return '';
      const t = String(rawText || '').trim();
      // Комментарий на русском: для фото-сообщений не показываем "Фото" как подпись по умолчанию.
      if (isPhoto && (t === '' || t === 'Фото')) return '';
      return formatMessageText(rawText, { linkify });
    },
    [isPhoto, isVoice, linkify, rawText]
  );
  
  // Время сообщения
  const timeString = formatMessageTime(message?.created_at || '');
  const fullDateTime = formatFullDateTime(message?.created_at || '');
  // Комментарий на русском: отметка "изменено" (если бэк вернул edited_at).
  const editedAt = (message as any)?.edited_at;
  const isEdited = !!(editedAt && String(editedAt).trim());
  
  // Статус сообщения
  const status: MessageStatus = message?.status || (message?.is_read ? 'read' : 'delivered');
  // Комментарий на русском: "не доставлено" = pending/sending/error.
  // UX: показываем как обычное сообщение, но с “размытыми” углами (подвешенное состояние).
  const isUndeliveredVisual = isOwn && (status === 'pending' || status === 'sending' || status === 'error');
  const undeliveredCornerClass = `${isOwn ? 'rounded-br-md' : 'rounded-bl-md'} ${isGrouped ? (isOwn ? 'rounded-tr-md' : 'rounded-tl-md') : ''}`;
  const statusIcon = getMessageStatusIcon(status);

  // ===== Реакции: “штамп” эффект (как Telegram на iOS) =====
  const [reactionStamp, setReactionStamp] = useState<{ key: number; emoji: string } | null>(null);
  const stampKeyRef = useRef(0);
  const stampTRef = useRef<number | null>(null);
  const triggerReactionStamp = useCallback((emoji: string) => {
    if (!emoji) return;
    stampKeyRef.current += 1;
    const k = stampKeyRef.current;
    setReactionStamp({ key: k, emoji });
    try {
      if (stampTRef.current != null) window.clearTimeout(stampTRef.current);
      stampTRef.current = window.setTimeout(() => {
        stampTRef.current = null;
        setReactionStamp((cur) => (cur && cur.key === k ? null : cur));
      }, 700);
    } catch {}
  }, []);
  useEffect(() => {
    return () => {
      try {
        if (stampTRef.current != null) window.clearTimeout(stampTRef.current);
      } catch {}
      stampTRef.current = null;
    };
  }, []);

  const applyReaction = useCallback(
    (emoRaw: string) => {
      if (!onReact) return;
      const emo = String(emoRaw || '').trim();
      if (!emo) return;
      // Комментарий на русском: “штамп” показываем только когда пользователь СТАВИТ реакцию (а не снимает).
      const wasMe = reactions.some((r: any) => String(r?.emoji || '').trim() === emo && !!r?.me);
      if (!wasMe) triggerReactionStamp(emo);
      haptic(10);
      onReact(message.id, emo);
    },
    [haptic, message.id, onReact, reactions, triggerReactionStamp]
  );
  
  // Обработчик контекстного меню
  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setShowMenu(true);
  }, []);
  
  // Закрыть меню при клике вне
  const closeMenu = useCallback(() => setShowMenu(false), []);
  const closeDeleteConfirm = useCallback(() => setShowDeleteConfirm(false), []);

  const copyMessageToClipboard = useCallback(async () => {
    // Комментарий на русском: копируем “чистый” текст, а для голосовых — ссылку (если есть).
    const t = isVoice ? (voiceUrl ? resolveVoiceUrl(voiceUrl) : '') : String(rawText || '');
    const text = t || String(rawText || '');
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      try {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.setAttribute('readonly', 'true');
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      } catch {
        // ignore
      }
    }
  }, [isVoice, rawText, voiceUrl]);

  const recalcMenuPos = useCallback(() => {
    // Комментарий на русском: меню делаем fixed и считаем координаты от пузырька, чтобы оно не клипалось скроллом и не пряталось под нижней панелью.
    const bubble = bubbleRef.current;
    const menu = menuRef.current;
    if (!bubble || !menu) return;
    const reactionsMenu = reactionsMenuRef.current;

    const br = bubble.getBoundingClientRect();
    const mr = menu.getBoundingClientRect();
    const rr = reactionsMenu ? reactionsMenu.getBoundingClientRect() : null;
    const compose = document.querySelector('[data-compose-bubble]') as HTMLElement | null;
    const composeTop = compose ? compose.getBoundingClientRect().top : window.innerHeight;
    const pad = 8;
    const gap = 8;

    // Слева от своего сообщения / справа от чужого.
    const w = Math.max(mr.width, rr ? rr.width : 0);
    let left = isOwn ? br.left - gap - w : br.right + gap;
    left = Math.max(pad, Math.min(left, window.innerWidth - pad - w));

    // Комментарий на русском: общий блок (реакции сверху + меню действий снизу).
    const anchorY = br.top + br.height / 2;
    const bottomLimit = Math.min(window.innerHeight - pad, composeTop - pad);
    const rh = rr ? rr.height : 0;
    const blockH = mr.height + (rr ? rh + gap : 0);
    let blockTop = anchorY - blockH / 2;
    const maxTop = bottomLimit - blockH;
    blockTop = Math.max(pad, Math.min(blockTop, Math.max(pad, maxTop)));

    if (rr) setReactionsMenuPos({ left: Math.round(left), top: Math.round(blockTop) });
    else setReactionsMenuPos(null);
    const menuTop = rr ? blockTop + rh + gap : blockTop;
    setMenuPos({ left: Math.round(left), top: Math.round(menuTop) });
  }, [isOwn]);

  useLayoutEffect(() => {
    if (!showMenu) {
      setMenuPos(null);
      setReactionsMenuPos(null);
      return;
    }
    recalcMenuPos();
  }, [showMenu, recalcMenuPos]);

  useEffect(() => {
    if (!showMenu) return;
    const onResize = () => recalcMenuPos();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [showMenu, recalcMenuPos]);

  useEffect(() => {
    // Комментарий на русском: закрываем контекстное меню при любом “внешнем” действии (тап, скролл, колесо, ESC).
    // Зачем: меню не должно висеть, когда пользователь пытается взаимодействовать с чатом.
    if (!showMenu) return;
    const isInsideMenus = (t: Node | null) => {
      if (!t) return false;
      return !!(menuRef.current?.contains(t) || reactionsMenuRef.current?.contains(t));
    };
    const onPointerDown = (e: Event) => {
      const t = e.target as Node | null;
      if (isInsideMenus(t)) return; // внутри меню — не закрываем (нужно для клика/скролла)
      closeMenu();
    };
    const onAnyScroll = (e: Event) => {
      const t = e.target as Node | null;
      if (isInsideMenus(t)) return; // Комментарий на русском: даём горизонтально скроллить реакции.
      closeMenu();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeMenu();
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('scroll', onAnyScroll, true);
    document.addEventListener('wheel', onAnyScroll, { capture: true, passive: true });
    document.addEventListener('touchmove', onAnyScroll, { capture: true, passive: true });
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('scroll', onAnyScroll, true);
      document.removeEventListener('wheel', onAnyScroll, true);
      document.removeEventListener('touchmove', onAnyScroll, true);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [closeMenu, showMenu]);

  function haptic(pattern: number | number[]) {
    // Комментарий на русском: вибро-хаптик работает не везде (iOS Safari обычно не поддерживает).
    try {
      const v = (globalThis as any)?.navigator?.vibrate;
      if (typeof v === 'function') v.call(navigator, pattern);
    } catch {}
  }

  const clearPressT = () => {
    if (pressTRef.current != null) {
      window.clearTimeout(pressTRef.current);
      pressTRef.current = null;
    }
  };

  const clearScaleT = () => {
    if (scaleTRef.current != null) {
      window.clearTimeout(scaleTRef.current);
      scaleTRef.current = null;
    }
  };

  const handlePointerDown = (e: React.PointerEvent) => {
    if ((e as any)?.button === 2) return; // ПКМ — контекстное меню отдельно
    pressActiveRef.current = true;
    startXRef.current = e.clientX;
    startYRef.current = e.clientY;
    swipeRawRef.current = 0;
    swipeCaptureIdRef.current = null;
    setPressScale(false);
    clearScaleT();

    // Комментарий на русском: свайп-ответ делаем только на touch/pen (на мыши мешает выделению текста).
    const t = e.target as HTMLElement | null;
    const startedOnWave = !!t?.closest?.('[data-voice-wave]');
    const startedOnButton = !!t?.closest?.('button');
    const allowHoldUi = !startedOnWave && !startedOnButton;
    const isTouch = e.pointerType === 'touch' || e.pointerType === 'pen';
    // Комментарий на русском: НЕ включаем swipe-reply при нажатии на кнопки (иначе ломается onClick у play/скорости на iOS/Android).
    const canSwipe = !!onReply && !startedOnWave && !startedOnButton && isTouch;
    swipeAllowedRef.current = canSwipe;
    // Комментарий на русском: важно — НЕ делаем setPointerCapture здесь, иначе iOS Safari начинает “дёргать” скролл.
    // Захват делаем в pointermove, когда уверены, что это горизонтальный свайп, а не вертикальный скролл.

    // Комментарий на русском: лёгкое увеличение пузырька при удержании.
    if (allowHoldUi && isTouch) {
      scaleTRef.current = window.setTimeout(() => {
        scaleTRef.current = null;
        if (pressActiveRef.current) setPressScale(true);
      }, 110);
    }

    if (!isTouch || !allowHoldUi) return;
    clearPressT();
    pressTRef.current = window.setTimeout(() => {
      pressTRef.current = null;
      if (!pressActiveRef.current) return;
      if (swipeXRef.current > 8) return;
      setShowReactions(false);
      setPressScale(true);
      haptic(10);
      setShowMenu(true);
    }, 420);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    const dx = e.clientX - startXRef.current;
    const dy = e.clientY - startYRef.current;
    if (Math.abs(dx) > 8 || Math.abs(dy) > 8) {
      clearPressT();
      clearScaleT();
      setPressScale(false);
    }

    if (!swipeAllowedRef.current) return;
    // Комментарий на русском: вертикальный скролл важнее. Порог делаем чуть “жёстче” для iOS,
    // чтобы небольшой диагональный старт не включал swipe-UI и не вызывал микро‑прыжок скролла.
    if (Math.abs(dy) > Math.abs(dx) + 6) return;

    const dir = isOwn ? -1 : 1; // к центру
    const toward = dx * dir;
    if (toward <= 0) {
      swipeRawRef.current = 0;
      swipeArmedRef.current = false;
      setSwipeRaw(0);
      setSwipe(0);
      return;
    }

    // Комментарий на русском: захватываем pointer как только уверены, что это горизонтальный свайп (минимум 1px к центру).
    if (swipeCaptureIdRef.current == null) {
      swipeCaptureIdRef.current = e.pointerId;
      setSwipeDown(true);
      setSwipe(0);
      try {
        (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
      } catch {}
    }

    // Комментарий на русском: “вес” — нелинейное сопротивление, чтобы жест ощущался плотнее.
    const raw = Math.min(420, toward);
    swipeRawRef.current = raw;
    setSwipeRaw(raw);
    const max = 72;
    const weighted = max * (1 - Math.exp(-raw / (42 * SWIPE_WEIGHT)));
    const next = Math.min(max, Math.max(0, weighted));
    setSwipe(next);
    // Комментарий на русском: “щёлчок” (вибро) когда дошли до порога ответа (по движению пальца).
    const armedNow = raw >= SWIPE_REPLY_THRESHOLD;
    if (armedNow && !swipeArmedRef.current) haptic(8);
    swipeArmedRef.current = armedNow;
  };

  const endPointer = (e: React.PointerEvent) => {
    pressActiveRef.current = false;
    clearPressT();
    clearScaleT();
    setPressScale(false);
    if (swipeAllowedRef.current) {
      const v = swipeXRef.current;
      const raw = swipeRawRef.current;
      swipeAllowedRef.current = false;
      if (swipeCaptureIdRef.current === e.pointerId) {
        swipeCaptureIdRef.current = null;
        setSwipeDown(false);
        setSwipe(0);
        setSwipeRaw(0);
        swipeRawRef.current = 0;
        swipeArmedRef.current = false;
        if (v >= SWIPE_REPLY_THRESHOLD || raw >= SWIPE_REPLY_THRESHOLD) {
          haptic([12]);
          onReply?.(message);
        }
        try {
          (e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId);
        } catch {}
      }
    }
  };
  
  // Защита от отсутствия данных
  if (!message) return null;
  
  return (
    <div 
      // Комментарий на русском: убраны mt-* margins — по документации Virtuoso margins мешают измерениям и вызывают дёргания.
      // Отступы теперь задаются через paddingTop в VirtuosoMessageList.
      className={`flex ${isOwn ? 'justify-end' : 'justify-start'} ${hasReactions ? 'pb-7' : ''} group relative`}
      onContextMenu={handleContextMenu}
      data-message-id={message.id}
    >
      {showReactions && onReact && (
        <>
          <div className="fixed inset-0 z-40" onPointerDown={() => { setShowReactions(false); setPressScale(false); }} />
          <div className={`absolute z-50 ${isOwn ? 'right-0' : 'left-0'} -top-14`}>
            <div
              className="flex items-center gap-1 px-2 py-1 rounded-full bg-site-bg/90 border border-site-border-control/40 shadow-xl backdrop-blur overflow-x-auto max-w-[80vw]"
              style={{ WebkitOverflowScrolling: 'touch' }}
            >
              {quickReactions.map((emo) => (
                <button
                  key={emo}
                  type="button"
                  onClick={() => {
                              applyReaction(emo);
                    setShowReactions(false);
                    setPressScale(false);
                  }}
                            className="w-12 h-12 rounded-full hover:bg-site-text/10 transition-colors flex items-center justify-center text-2xl shrink-0"
                  aria-label={`Реакция ${emo}`}
                  title="Реакция"
                >
                  {emo}
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Аватар собеседника */}
      {!isOwn && showAvatar && !isGrouped && message.from_user && (
        <div className="flex-shrink-0 mr-2">
          <AvatarFx
            sizeClass="w-8 h-8"
            avatarUrl={message.from_user.avatar_url || null}
            alt={message.from_user.display_name || message.from_user.username || ''}
            fallback={
              message.from_user.display_name?.[0]?.toUpperCase() ||
              message.from_user.username?.[0]?.toUpperCase() ||
              '?'
            }
            isOnline={peerOnline}
            fxColor={message.from_user.avatar_fx_color}
            fxThickness={message.from_user.avatar_fx_thickness}
            fxGlow={message.from_user.avatar_fx_glow}
          />
        </div>
      )}
      
      {/* Пустое место для выравнивания сгруппированных сообщений */}
      {/* Комментарий на русском: если аватары включены, но сообщение сгруппировано — держим одинаковый отступ слева. */}
      {!isOwn && showAvatar && isGrouped && (
        <div className="w-8 mr-2 flex-shrink-0" />
      )}
      
      {/* Пузырек сообщения */}
      <div className="relative">
        {/* Комментарий на русском: "размытые углы" для неотправленных/не доставленных сообщений (pending/sending/error).
           Сделано отдельным слоем позади пузырька, чтобы текст/контент не размывался. */}
        {isUndeliveredVisual && (
          <div
            aria-hidden="true"
            className={`pointer-events-none absolute -inset-1 rounded-2xl ${undeliveredCornerClass} blur-[10px] opacity-35 lisad-msg-bubble-ghost`}
          />
        )}

        {/* Комментарий на русском: “штамп” эффекта реакции — вспышка поверх сообщения. */}
        {reactionStamp && (
          <div className="pointer-events-none absolute -left-2 -bottom-2 z-30">
            <div key={reactionStamp.key} className="lisad-react-stamp">
              {reactionStamp.emoji}
            </div>
          </div>
        )}
        {/* Комментарий на русском: индикатор "ответ" (появляется в зазоре при свайпе). */}
        {onReply && swipeX > 1 && (
          <div
            className={`absolute top-1/2 -translate-y-1/2 ${isOwn ? 'left-full ml-2' : 'right-full mr-2'} pointer-events-none`}
            aria-hidden="true"
          >
            {(() => {
              const p = Math.min(1, (swipeRaw || 0) / SWIPE_REPLY_THRESHOLD);
              const armed = (swipeRaw || 0) >= SWIPE_REPLY_THRESHOLD;
              const sc = 0.92 + p * 0.12 + (armed ? 0.06 : 0);
              return (
                <div
                  className={`w-9 h-9 rounded-full flex items-center justify-center border shadow-lg ${
                    armed ? 'bg-site-accent/70 border-site-accent/50' : 'bg-site-bg/65 border-site-border'
                  }`}
                  style={{ opacity: 0.25 + p * 0.75, transform: `scale(${sc})` }}
                >
                  ↩
                </div>
              );
            })()}
          </div>
        )}

        <div
          ref={bubbleRef}
          className={`
            ${isMedia ? 'max-w-[min(92vw,520px)] px-0 py-0 bg-transparent' : 'max-w-xs lg:max-w-md xl:max-w-lg px-[var(--dm-pad-x,1rem)] py-[var(--dm-pad-y,0.5rem)]'}
            rounded-2xl relative select-none
            transition-all duration-150
            ${isSelected ? 'ring-2 ring-site-accent/45' : (pressScale || showReactions || showMenu ? 'ring-1 ring-site-border-control' : '')}
            ${pressScale || showReactions || showMenu || isSelected ? 'shadow-[0_10px_28px_rgba(255,255,255,0.14)]' : ''}
            ${isMedia ? 'text-site-text' : (isOwn ? 'rounded-br-md lisad-msg-bubble lisad-msg-bubble--me' : 'rounded-bl-md lisad-msg-bubble lisad-msg-bubble--peer')}
            ${isGrouped ? (isOwn ? 'rounded-tr-md' : 'rounded-tl-md') : ''}
          `}
          title={fullDateTime}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={endPointer}
          onPointerCancel={endPointer}
          onPointerLeave={() => {
            pressActiveRef.current = false;
            clearPressT();
            clearScaleT();
            swipeArmedRef.current = false;
            setSwipeRaw(0);
            setPressScale(false);
          }}
          onClick={(e) => {
            // Комментарий на русском: в режиме выбора — тап по пузырьку переключает выделение (как в Telegram).
            if (!selectionMode || !onToggleSelect) return;
            const t = e.target as HTMLElement | null;
            if (!t) return;
            if (t.closest('a') || t.closest('button') || t.closest('[data-voice-wave]')) return;
            e.preventDefault();
            e.stopPropagation();
            haptic(8);
            onToggleSelect(message);
          }}
          style={{
            ...(!isMedia
              ? (() => {
                  // Комментарий на русском: в комнатах цвет сообщений собеседников = их bubble_color (как они настроили "мои").
                  // В личке цвет собеседника задаётся настройками текущего пользователя (через CSS-переменные на уровне страницы).
                  if (!chatId.startsWith('room:')) return undefined;
                  if (isOwn) return undefined;
                  const triplet = hexToTriplet((message as any)?.from_user?.bubble_color);
                  // Комментарий на русском: задаём именно peer-переменную, чтобы правило .lisad-msg-bubble--peer корректно её подхватило.
                  return triplet ? ({ ['--mb-peer-rgb' as any]: triplet } as any) : undefined;
                })()
              : undefined),
            transform: (() => {
              const tx = (isOwn ? -1 : 1) * (swipeX || 0);
              const sc = pressScale || showReactions ? 1.03 : 1;
              if (!tx && sc === 1) return undefined;
              return `translateX(${tx}px) scale(${sc})`;
            })(),
            transition: swipeDown ? 'none' : 'transform 190ms cubic-bezier(0.34, 1.56, 0.64, 1)',
            touchAction: 'pan-y',
          }}
        >
        {/* Комментарий на русском: лайк на десктопе — внутри пузырька, слева снизу. */}
        {onReact && (
          <button
            type="button"
            onClick={() => {
              setShowMenu(false);
              setPressScale(false);
              setShowReactions(true);
            }}
            className={`absolute left-2 bottom-2 z-10 opacity-0 group-hover:opacity-100 transition-opacity rounded-full w-8 h-8 flex items-center justify-center ${
              isOwn ? 'text-site-text/80 hover:text-site-text hover:bg-site-text/10' : 'text-site-text/85 hover:text-site-text hover:bg-site-text/10'
            }`}
            title="Реакция"
            aria-label="Реакция"
          >
            <span className="text-lg leading-none"><UiIcon name="ThumbsUp" /></span>
          </button>
        )}

        {/* Комментарий на русском: цитата ответа (reply_to) */}
        {reply && (
          <div className={`mb-2 px-2 py-1 rounded-lg border-l-2 ${isOwn ? 'bg-site-text/10 border-site-border' : 'bg-black/15 border-site-border'}`}>
            <div className="text-[11px] font-semibold text-site-text/90 truncate">
              {(() => {
                const myId = isOwn ? message?.from_user?.id : message?.to_user?.id;
                const au = reply?.from_user;
                if (myId && au?.id === myId) return 'Вы';
                return au?.display_name || au?.username || 'Сообщение';
              })()}
            </div>
            <div className="text-[11px] text-site-text/70 truncate">
              {(() => {
                const t = String(reply?.message || '').trim();
                const isV =
                  t.startsWith('blob:') ||
                  t.includes('/chat_voice/') ||
                  t.includes('/uploads/chat_voice/') ||
                  t.includes('/media/chat_voice/');
                return isV ? 'Голосовое сообщение' : (t || '...');
              })()}
            </div>
          </div>
        )}

        {/* Комментарий на русском: фото — отдельный рендер, голосовые — audio, остальное — текст. */}
        {isVoice ? (
          <div className="w-72 max-w-full">
            {(() => {
              // Комментарий на русском: проигрывание ТОЛЬКО из локального источника (blob/data).
              // Зачем: офлайн-режим + стабильность (серверный URL может требовать авторизацию/быть недоступен).
              const localSrc =
                cachedVoiceSrc ||
                (directVoiceSrc && /^https?:\/\//.test(directVoiceSrc) ? directVoiceSrc : '') ||
                (fallbackVoiceSrc && (fallbackVoiceSrc.startsWith('blob:') || fallbackVoiceSrc.startsWith('data:')) ? fallbackVoiceSrc : '') ||
                (audioSrc && (audioSrc.startsWith('blob:') || audioSrc.startsWith('data:')) ? audioSrc : '');
              return (
            <audio
              ref={audioRef}
              preload="metadata"
              playsInline
              src={localSrc || undefined}
              onLoadStart={() => {
                if (cachedVoiceSrc) return;
                // Комментарий на русском: если серверный файл “не сразу” доступен, переходим на локальный blob.
                if (!fallbackVoiceSrc || audioSrc !== voiceSrc) return;
                clearLoadT();
                loadTRef.current = window.setTimeout(() => {
                  setAudioSrc(fallbackVoiceSrc);
                }, 2200);
              }}
              onLoadedMetadata={clearLoadT}
              onCanPlay={clearLoadT}
              onError={(e) => {
                const audio = e.currentTarget;
                const errorCode = audio?.error?.code;
                const errorMsg = audio?.error?.message || 'Unknown audio error';
                const usedSrc = cachedVoiceSrc || fallbackVoiceSrc || audioSrc || directVoiceSrc || '';
                const isBlobSrc = usedSrc && (usedSrc.startsWith('blob:') || usedSrc.startsWith('data:'));
                const serverUrl =
                  voiceSrc && !voiceSrc.startsWith('blob:') && !voiceSrc.startsWith('data:') && /^https?:\/\//.test(voiceSrc) ? voiceSrc : '';
                logVoiceError('Ошибка воспроизведения audio элемента', {
                  errorCode,
                  errorMsg,
                  currentSrc: audio?.currentSrc,
                  networkState: audio?.networkState,
                  readyState: audio?.readyState,
                  messageId: message?.id,
                  localSrc: usedSrc,
                  voiceSrc,
                });
                // Комментарий на русском:
                // Если blob/data сломался (часто после обрыва сети или на Safari/iOS), надо:
                // - удалить объектный URL + кэш CacheStorage (чтобы не застрять на "битом" blob)
                // - и попытаться играть напрямую по server URL.
                // Важно: делаем это даже если directVoiceSrc уже равен serverUrl (cachedVoiceSrc может иметь приоритет в localSrc).
                if (serverUrl && usedSrc && isBlobSrc) {
                  try {
                    if (cachedVoiceUrlRef.current) URL.revokeObjectURL(cachedVoiceUrlRef.current);
                  } catch {}
                  cachedVoiceUrlRef.current = '';
                  cachedVoiceKeyRef.current = '';
                  // Комментарий на русском: фикс гонки — делаем синхронный commit, чтобы React не "вернул" старый blob обратно в src.
                  flushSync(() => setCachedVoiceSrc(''));
                  // Комментарий на русском: чистим возможный битый blob из CacheStorage, чтобы новый заход взял свежий файл.
                  void deleteCachedVoice(serverUrl);

                  // Комментарий на русском: переключаемся на прямой URL и пробуем воспроизвести ТОЛЬКО если пользователь намеренно запустил воспроизведение.
                  // Это предотвращает автоплей при загрузке страницы, когда кэшированный blob становится невалидным.
                  flushSync(() => setDirectVoiceSrc(serverUrl));
                  if (userInitiatedPlayRef.current && isOnline()) {
                    unlockVoicePlaybackOnce();
                    try {
                      audio.src = serverUrl;
                      audio.load?.();
                      const p = audio.play();
                      if (p && typeof (p as any).catch === 'function') (p as any).catch(() => {});
                    } catch {}
                  }
                  return;
                }
                if (!fallbackVoiceSrc || audioSrc === fallbackVoiceSrc) return;
                clearLoadT();
                setAudioSrc(fallbackVoiceSrc);
              }}
              className="hidden"
            />
              );
            })()}

            <div className="flex items-center gap-3">
              {/* Комментарий на русском: кнопка Play с прогрессом загрузки как в Telegram. */}
              <button
                type="button"
                onClick={togglePlay}
                className="w-12 h-12 rounded-full border-2 border-site-border flex items-center justify-center shrink-0 hover:bg-site-text/10 transition-colors relative"
                aria-label={isDownloading ? `Загрузка ${downloadProgress !== null && downloadProgress >= 0 ? downloadProgress + '%' : '...'}` : isVoiceLoading ? 'Загрузка...' : isPlaying ? 'Пауза' : 'Проиграть'}
                title={isDownloading ? `Загрузка ${downloadProgress !== null && downloadProgress >= 0 ? downloadProgress + '%' : '...'}` : isVoiceLoading ? 'Загрузка...' : isPlaying ? 'Пауза' : 'Проиграть'}
              >
                {/* Комментарий на русском: круговой прогресс загрузки (как в Telegram). */}
                {isDownloading && downloadProgress !== null && (
                  <svg className="absolute inset-0 w-full h-full -rotate-90" viewBox="0 0 48 48">
                    <circle
                      cx="24"
                      cy="24"
                      r="20"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeOpacity="0.2"
                    />
                    <circle
                      cx="24"
                      cy="24"
                      r="20"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeDasharray={`${2 * Math.PI * 20}`}
                      strokeDashoffset={`${2 * Math.PI * 20 * (1 - (downloadProgress >= 0 ? downloadProgress : 50) / 100)}`}
                      className="transition-all duration-150"
                    />
                  </svg>
                )}
                {isDownloading ? (
                  // Комментарий на русском: иконка "стоп" во время загрузки (можно отменить).
                  <span className="w-4 h-4 bg-site-text/80 rounded-sm" aria-hidden="true" />
                ) : isVoiceLoading ? (
                  <span className="w-6 h-6 border-2 border-site-border border-t-white/90 rounded-full animate-spin" aria-hidden="true" />
                ) : isPlaying ? (
                  <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor">
                    <rect x="6.5" y="5.5" width="4" height="13" rx="1" />
                    <rect x="13.5" y="5.5" width="4" height="13" rx="1" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                )}
              </button>

              <div className="flex-1 min-w-0">
                <div
                  ref={waveRef}
                  data-voice-wave
                  className={`relative h-7 flex items-end gap-[1px] select-none touch-none ${isVoiceLoading ? 'opacity-70' : ''}`}
                  onPointerDown={(e) => {
                    if (isVoiceLoading) return;
                    dragRef.current = true;
                    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
                    seekFromClientX(e.clientX);
                  }}
                  onPointerMove={(e) => {
                    if (isVoiceLoading) return;
                    if (!dragRef.current) return;
                    seekFromClientX(e.clientX);
                  }}
                  onPointerUp={(e) => {
                    dragRef.current = false;
                    try {
                      (e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId);
                    } catch {}
                  }}
                  onPointerCancel={(e) => {
                    dragRef.current = false;
                    try {
                      (e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId);
                    } catch {}
                  }}
                  title="Перемотка"
                >
                  {/* Комментарий на русском: две “слойные” волны — нижняя серая, верхняя белая (обрезается по --voice-pct). */}
                  <div className="absolute inset-0 flex items-end gap-[1px] pointer-events-none">
                    {waveBars.map((v, i) => {
                      const hPx = Math.round(6 + v * 16);
                      return (
                        <span
                          key={i}
                          ref={(el) => {
                            waveBarsBgRef.current[i] = el;
                          }}
                          className="flex-1 rounded-full bg-site-text/35 origin-bottom transition-transform duration-150"
                          style={{ height: `${hPx}px` }}
                        />
                      );
                    })}
                  </div>
                  {/* Комментарий на русском: белую волну НЕ растягиваем — держим полную ширину и обрезаем по clip-path. */}
                  <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
                    <div
                      className="absolute inset-0 overflow-hidden"
                      style={{ clipPath: 'inset(0 calc(100% - var(--voice-pct, 0.5%)) 0 0)' }}
                    >
                      <div className="h-full flex items-end gap-[1px]">
                        {waveBars.map((v, i) => {
                          const hPx = Math.round(6 + v * 16);
                          return (
                            <span
                              key={i}
                              ref={(el) => {
                                waveBarsFgRef.current[i] = el;
                              }}
                              className="flex-1 rounded-full bg-site-text/85 origin-bottom transition-transform duration-150"
                              style={{ height: `${hPx}px` }}
                            />
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  {/* Комментарий на русском: убрали белую “линию” — позицию показывает зелёный “подпрыгивающий” столбик. */}

                  {/* Комментарий на русском: загрузка плеера (волна/ползунок) — пока идёт подготовка/буферизация. */}
                  {isVoiceLoading && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none" aria-hidden="true">
                      <span className="w-4 h-4 border-2 border-site-border border-t-white/80 rounded-full animate-spin" />
                    </div>
                  )}
                </div>
                <div className="mt-1 flex items-center justify-between text-[11px] text-site-text/70 tabular-nums">
                  <span>{formatT(pos)}</span>
                  <span>{dur ? formatT(dur) : '0:00'}</span>
                </div>
              </div>

              <button
                type="button"
                onClick={toggleRate}
                className="px-2 py-1 text-[11px] font-semibold rounded-lg border border-site-border text-site-text/80 hover:bg-site-text/10 transition-colors shrink-0"
                title="Скорость"
                aria-label="Скорость воспроизведения"
              >
                {rate}x
              </button>
            </div>
          </div>
        ) : isPhoto ? (
          <div className="w-full">
            <button
              type="button"
              onPointerDown={handlePhotoPointerDown}
              onPointerMove={handlePhotoPointerMove}
              onPointerUp={handlePhotoPointerUp}
              onClick={() => {
                // Комментарий на русском: десктоп fallback (если pointerup не сработал).
                if (photoTapHandledRef.current) return;
                if (onOpenPhotoViewer && photoUrl) {
                  onOpenPhotoViewer(photoUrl, Number(message?.id || 0) || 0);
                  return;
                }
                photoViewerOpenRef.current = true;
                setPhotoViewerOpen(true);
                setPhotoViewerDragging(false);
                photoViewerDragYRef.current = 0;
                setPhotoViewerDragY(0);
              }}
              onContextMenu={handleContextMenu}
              className="block w-full"
              title="Открыть фото"
              aria-label="Открыть фото"
            >
              <div className="relative w-full">
                {photoSrc || localPhotoSrc ? (
                  <img
                    src={photoSrc || localPhotoSrc}
                    alt="Фото"
                    className="w-full max-h-[31vh] object-contain rounded-2xl border border-site-border"
                    // Комментарий на русском: fallback aspect-ratio если сервер не прислал размеры — убирает прыжок при загрузке.
                    style={photoAspectStyle || ({ aspectRatio: '4 / 3' } as any)}
                    loading="lazy"
                    draggable={false}
                  />
                ) : (
                  <div
                    className="w-full max-h-[31vh] rounded-2xl border border-site-border bg-site-surface/40 flex items-center justify-center photo-placeholder-skeleton"
                    style={photoAspectStyle || ({ aspectRatio: '4 / 3' } as any)}
                  >
                    {photoNeedsDownload ? (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); void downloadPhotoManual(); }}
                        className="w-14 h-14 rounded-full bg-site-bg/60 border border-site-border text-site-text/80 flex items-center justify-center hover:bg-site-surface/70 transition active:scale-[0.92] photo-download-btn"
                        title="Загрузить фото"
                        aria-label="Загрузить фото"
                      >
                        {/* Монохромная иконка загрузки */}
                        <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                          <polyline points="7 10 12 15 17 10" />
                          <line x1="12" y1="15" x2="12" y2="3" />
                        </svg>
                      </button>
                    ) : (
                      <div className="photo-loading-indicator">
                        {/* Монохромная анимация загрузки */}
                        <svg className="w-10 h-10 animate-spin-slow" viewBox="0 0 24 24" fill="none">
                          <circle className="opacity-20" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                          <path className="opacity-70" d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                      </div>
                    )}
                  </div>
                )}

                {/* Комментарий на русском: время + доставка внутри контента (в правом нижнем углу). */}
                <div className="absolute bottom-2 right-2 pointer-events-none">
                  <div className="px-2 py-1 rounded-full bg-black/45 backdrop-blur-sm border border-site-border text-[11px] leading-none text-site-text/90 flex items-center gap-1">
                    <span className="tabular-nums">{timeString}</span>
                    {isOwn && !!statusIcon && (
                      <span className={`ml-0.5 font-semibold ${status === 'read' ? 'text-site-accent' : status === 'error' ? 'text-site-danger' : 'text-site-text/85'}`}>
                        {statusIcon}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </button>

            {/* Комментарий на русском: встроенный просмотр фото (desktop+mobile), на мобильных — full screen + swipe down to close. */}
            {photoViewerOpen &&
              (typeof document !== 'undefined' && document.body
                ? createPortal(
                    (() => {
                      const u = String(photoSrc || localPhotoSrc || '').trim();
                      const dy = Math.max(0, Math.round(photoViewerDragY || 0));
                      const dragK = Math.min(1, dy / 240);
                      const bgOpacity = Math.max(0.18, 0.75 - dragK * 0.45);
                      return (
                        <div className="fixed inset-0 z-[12000]">
                          <div
                            className="absolute inset-0 backdrop-blur-md"
                            style={{ backgroundColor: `rgba(0,0,0,${bgOpacity})` }}
                            onClick={closePhotoViewer}
                            aria-hidden="true"
                          />

                          {/* Верхняя панель (десктоп) / Нижняя панель (мобильные) */}
                          <div className={`absolute z-[12002] flex items-center gap-2 ${isPhoneViewport ? 'bottom-4 left-1/2 -translate-x-1/2' : 'top-3 right-3'}`}>
                            {/* Комментарий на русском: кнопки зума для планшетов и десктопа. */}
                            {!isPhoneViewport && (
                              <>
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); zoomOut(); }}
                                  className="w-11 h-11 rounded-full glass-btn text-site-text/90 hover:text-site-text flex items-center justify-center transition active:scale-[0.92] text-xl font-bold"
                                  title="Уменьшить"
                                  aria-label="Уменьшить"
                                >
                                  −
                                </button>
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); zoomReset(); }}
                                  className="w-11 h-11 rounded-full glass-btn text-site-text/90 hover:text-site-text flex items-center justify-center transition active:scale-[0.92]"
                                  title="Сбросить масштаб"
                                  aria-label="Сбросить масштаб"
                                >
                                  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="11" cy="11" r="8" />
                                    <path d="m21 21-4.3-4.3" />
                                  </svg>
                                </button>
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); zoomIn(); }}
                                  className="w-11 h-11 rounded-full glass-btn text-site-text/90 hover:text-site-text flex items-center justify-center transition active:scale-[0.92] text-xl font-bold"
                                  title="Увеличить"
                                  aria-label="Увеличить"
                                >
                                  +
                                </button>
                                <div className="w-px h-6 bg-site-text/20" />
                              </>
                            )}
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                downloadPhoto();
                              }}
                              className="w-11 h-11 rounded-full glass-btn text-site-text/90 hover:text-site-text flex items-center justify-center transition active:scale-[0.92]"
                              title="Скачать оригинал"
                              aria-label="Скачать оригинал"
                            >
                              <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                <polyline points="7,10 12,15 17,10" />
                                <line x1="12" y1="15" x2="12" y2="3" />
                              </svg>
                            </button>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                closePhotoViewer();
                              }}
                              className="w-11 h-11 rounded-full glass-btn text-site-text/90 hover:text-site-text flex items-center justify-center transition active:scale-[0.92]"
                              title="Закрыть"
                              aria-label="Закрыть"
                            >
                              ✕
                            </button>
                          </div>

                          {/* Контент */}
                          <div
                            className="absolute inset-0 z-[12001] flex items-center justify-center px-0 py-0 sm:p-10"
                            onClick={(e) => e.stopPropagation()}
                            onWheel={(e) => {
                              // Комментарий на русском: зум колесиком мыши (только десктоп).
                              if (isPhoneViewport) return;
                              e.preventDefault();
                              const delta = e.deltaY > 0 ? -0.25 : 0.25;
                              setPhotoViewerScale((s) => {
                                const next = Math.max(1, Math.min(4, s + delta));
                                if (next <= 1) setPhotoViewerTranslate({ x: 0, y: 0 });
                                return next;
                              });
                            }}
                            onTouchStart={(e) => {
                              // Комментарий на русском: зум двумя пальцами (pinch-to-zoom).
                              if (e.touches.length === 2) {
                                const t1 = e.touches[0];
                                const t2 = e.touches[1];
                                const dist = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
                                const centerX = (t1.clientX + t2.clientX) / 2;
                                const centerY = (t1.clientY + t2.clientY) / 2;
                                const img = photoViewerImgRef.current;
                                if (img) {
                                  const rect = img.getBoundingClientRect();
                                  const imgCenterX = rect.left + rect.width / 2;
                                  const imgCenterY = rect.top + rect.height / 2;
                                  photoViewerPinchRef.current = {
                                    distance: dist,
                                    center: { x: centerX - imgCenterX, y: centerY - imgCenterY },
                                    initialScale: photoViewerScale,
                                    initialTranslate: { ...photoViewerTranslate },
                                  };
                                }
                              } else if (e.touches.length === 1 && photoViewerScale <= 1) {
                                // Комментарий на русском: свайп вниз для закрытия (только если не зум).
                                const t = e.touches[0];
                                photoViewerPointerIdRef.current = t.identifier;
                                photoViewerStartRef.current = { x: t.clientX, y: t.clientY };
                                setPhotoViewerDragging(true);
                              }
                            }}
                            onTouchMove={(e) => {
                              e.preventDefault();
                              // Комментарий на русском: зум двумя пальцами.
                              if (e.touches.length === 2 && photoViewerPinchRef.current) {
                                const t1 = e.touches[0];
                                const t2 = e.touches[1];
                                const dist = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
                                const pinch = photoViewerPinchRef.current;
                                const scale = Math.max(1, Math.min(4, (pinch.initialScale * dist) / pinch.distance));
                                setPhotoViewerScale(scale);
                                if (scale > 1) {
                                  const centerX = (t1.clientX + t2.clientX) / 2;
                                  const centerY = (t1.clientY + t2.clientY) / 2;
                                  const img = photoViewerImgRef.current;
                                  if (img) {
                                    const rect = img.getBoundingClientRect();
                                    const imgCenterX = rect.left + rect.width / 2;
                                    const imgCenterY = rect.top + rect.height / 2;
                                    const dx = centerX - imgCenterX - pinch.center.x;
                                    const dy = centerY - imgCenterY - pinch.center.y;
                                    setPhotoViewerTranslate({
                                      x: pinch.initialTranslate.x + dx / scale,
                                      y: pinch.initialTranslate.y + dy / scale,
                                    });
                                  }
                                } else {
                                  setPhotoViewerTranslate({ x: 0, y: 0 });
                                }
                                return;
                              }
                              // Комментарий на русском: свайп вниз одним пальцем (только если не зум).
                              if (e.touches.length === 1 && photoViewerScale <= 1 && photoViewerDragging) {
                                const t = e.touches[0];
                                if (photoViewerPointerIdRef.current !== t.identifier) return;
                                const st = photoViewerStartRef.current;
                                if (!st) return;
                                const dx = t.clientX - st.x;
                                const dyRaw = t.clientY - st.y;
                                // Комментарий на русском: если жест больше горизонтальный — игнорируем.
                                if (Math.abs(dx) > Math.abs(dyRaw) * 0.9) return;
                                const dyNext = Math.max(0, dyRaw);
                                photoViewerDragYRef.current = dyNext;
                                setPhotoViewerDragY(dyNext);
                              }
                            }}
                            onTouchEnd={(e) => {
                              // Комментарий на русском: завершение зума.
                              if (e.touches.length < 2) {
                                photoViewerPinchRef.current = null;
                              }
                              // Комментарий на русском: завершение свайпа вниз.
                              if (e.touches.length === 0 && photoViewerDragging && photoViewerScale <= 1) {
                                photoViewerPointerIdRef.current = null;
                                photoViewerStartRef.current = null;
                                setPhotoViewerDragging(false);
                                const dyFinal = photoViewerDragYRef.current || 0;
                                if (dyFinal > 140) {
                                  closePhotoViewer();
                                } else {
                                  photoViewerDragYRef.current = 0;
                                  setPhotoViewerDragY(0);
                                }
                              }
                            }}
                            onTouchCancel={(e) => {
                              photoViewerPinchRef.current = null;
                              photoViewerPointerIdRef.current = null;
                              photoViewerStartRef.current = null;
                              setPhotoViewerDragging(false);
                              photoViewerDragYRef.current = 0;
                              setPhotoViewerDragY(0);
                            }}
                          >
                            <div
                              className="max-w-full max-h-full"
                              onPointerDown={(e) => {
                                // Комментарий на русском: drag мышью при зуме на десктопе.
                                if (e.pointerType === 'mouse' && photoViewerScale > 1) {
                                  photoViewerPointerIdRef.current = e.pointerId;
                                  photoViewerStartRef.current = { x: e.clientX, y: e.clientY };
                                  setPhotoViewerDragging(true);
                                  try {
                                    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
                                  } catch {}
                                  return;
                                }
                                // Комментарий на русском: свайп вниз — только для touch (fallback для старых браузеров).
                                if (e.pointerType !== 'touch') return;
                                if (photoViewerScale > 1) return;
                                photoViewerPointerIdRef.current = e.pointerId;
                                photoViewerStartRef.current = { x: e.clientX, y: e.clientY };
                                setPhotoViewerDragging(true);
                                try {
                                  (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
                                } catch {}
                              }}
                              onPointerMove={(e) => {
                                if (!photoViewerDragging) return;
                                if (photoViewerPointerIdRef.current !== e.pointerId) return;
                                const st = photoViewerStartRef.current;
                                if (!st) return;
                                const dx = e.clientX - st.x;
                                const dyRaw = e.clientY - st.y;
                                // Комментарий на русском: drag при зуме на десктопе.
                                if (e.pointerType === 'mouse' && photoViewerScale > 1) {
                                  setPhotoViewerTranslate((t) => ({
                                    x: t.x + dx,
                                    y: t.y + dyRaw,
                                  }));
                                  photoViewerStartRef.current = { x: e.clientX, y: e.clientY };
                                  return;
                                }
                                // Комментарий на русском: свайп вниз для закрытия (touch).
                                if (photoViewerScale > 1) return;
                                if (Math.abs(dx) > Math.abs(dyRaw) * 0.9) return;
                                const dyNext = Math.max(0, dyRaw);
                                photoViewerDragYRef.current = dyNext;
                                setPhotoViewerDragY(dyNext);
                              }}
                              onPointerUp={(e) => {
                                if (photoViewerPointerIdRef.current !== e.pointerId) return;
                                try {
                                  (e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId);
                                } catch {}
                                photoViewerPointerIdRef.current = null;
                                photoViewerStartRef.current = null;
                                setPhotoViewerDragging(false);
                                // Комментарий на русском: для mouse при зуме просто останавливаем drag.
                                if (e.pointerType === 'mouse' && photoViewerScale > 1) return;
                                const dyFinal = photoViewerDragYRef.current || 0;
                                if (dyFinal > 140 && photoViewerScale <= 1) {
                                  closePhotoViewer();
                                } else {
                                  photoViewerDragYRef.current = 0;
                                  setPhotoViewerDragY(0);
                                }
                              }}
                              onPointerCancel={(e) => {
                                if (photoViewerPointerIdRef.current !== e.pointerId) return;
                                photoViewerPointerIdRef.current = null;
                                photoViewerStartRef.current = null;
                                setPhotoViewerDragging(false);
                                photoViewerDragYRef.current = 0;
                                setPhotoViewerDragY(0);
                              }}
                              style={{
                                touchAction: 'none',
                                cursor: photoViewerScale > 1 ? 'grab' : 'default',
                                transform: `translate3d(${photoViewerTranslate.x}px, ${photoViewerDragY + photoViewerTranslate.y}px, 0) scale(${photoViewerScale})`,
                                transition: photoViewerDragging || photoViewerPinchRef.current ? 'none' : 'transform 180ms ease-out',
                              }}
                            >
                              {u ? (
                                <img
                                  ref={photoViewerImgRef}
                                  src={u}
                                  alt="Фото"
                                  className="max-w-[100vw] max-h-[100vh] sm:max-w-[min(92vw,980px)] sm:max-h-[86vh] object-contain rounded-none sm:rounded-2xl border-0 sm:border sm:border-site-border shadow-[0_25px_90px_rgba(0,0,0,0.65)]"
                                  draggable={false}
                                  style={{ touchAction: 'none' }}
                                />
                              ) : (
                                <div
                                  className="w-[min(92vw,680px)] rounded-2xl border border-site-border bg-black/20 flex items-center justify-center"
                                  style={photoAspectStyle || ({ aspectRatio: '4 / 3' } as any)}
                                >
                                  <span className={`w-7 h-7 border-2 border-site-border border-t-white/80 rounded-full ${isPhotoLoading ? 'animate-spin' : ''}`} />
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })(),
                    document.body
                  )
                : null)}
            {formattedText ? (
              <div
                className="mt-2 px-1 whitespace-pre-wrap break-words select-text"
                style={{ fontSize: 'var(--dm-font,0.875rem)', lineHeight: 'var(--dm-leading,1.5)' }}
                dangerouslySetInnerHTML={{ __html: formattedText }}
              />
            ) : null}
          </div>
        ) : isFile ? (
          <div className="w-full">
            <div
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                // Комментарий на русском: на десктопе — открываем (системная программа/просмотр), на телефоне — скачиваем.
                const isPhone = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(max-width: 639px)').matches;
                if (isPhone) void openDocViewer();
                else void openFile();
              }}
              onKeyDown={(e) => {
                if (e.key !== 'Enter' && e.key !== ' ') return;
                e.preventDefault();
                e.stopPropagation();
                const isPhone = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(max-width: 639px)').matches;
                if (isPhone) void openDocViewer();
                else void openFile();
              }}
              onContextMenu={handleContextMenu}
              aria-disabled={!fileUrl || fileDownloading}
              className="block w-full cursor-pointer select-none"
              title={fileUrl ? 'Открыть файл' : 'Файл загружается...'}
              aria-label="Открыть файл"
            >
              <div className="relative w-full">
                <div className="w-full rounded-2xl border border-site-border bg-site-surface/40 hover:bg-site-surface/55 transition-colors px-3 py-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl border flex items-center justify-center shrink-0 text-[11px] font-extrabold tracking-wide ${fileBadge.cls}`}>
                      {fileBadge.label}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-site-text/95 truncate">{fileDownloadName}</div>
                      <div className="text-[11px] text-site-text/65 truncate">
                        {formatBytes(fileAttRaw?.size || 0)}{fileExt ? ` · ${fileExt.toUpperCase()}` : (String(fileAttRaw?.mime || '').trim() ? ` · ${String(fileAttRaw?.mime || '').trim()}` : '')}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        void downloadFile();
                      }}
                      disabled={!fileUrl || fileDownloading}
                      className="w-10 h-10 rounded-xl bg-black/25 border border-site-border flex items-center justify-center shrink-0 hover:bg-black/35 transition active:scale-[0.95] disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Скачать"
                      aria-label="Скачать"
                    >
                      {fileDownloading ? (
                        <span className="w-5 h-5 border-2 border-site-border border-t-white/80 rounded-full animate-spin" />
                      ) : (
                        <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                          <polyline points="7,10 12,15 17,10" />
                          <line x1="12" y1="15" x2="12" y2="3" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>

                {/* Комментарий на русском: время + доставка внутри контента (в правом нижнем углу). */}
                <div className="absolute bottom-2 right-2 pointer-events-none">
                  <div className="px-2 py-1 rounded-full bg-black/45 backdrop-blur-sm border border-site-border text-[11px] leading-none text-site-text/90 flex items-center gap-1">
                    <span className="tabular-nums">{timeString}</span>
                    {isOwn && !!statusIcon && (
                      <span className={`ml-0.5 font-semibold ${status === 'read' ? 'text-site-accent' : status === 'error' ? 'text-site-danger' : 'text-site-text/85'}`}>
                        {statusIcon}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
            {docViewerOpen &&
              (typeof document !== 'undefined' && document.body
                ? createPortal(
                    <div className="fixed inset-0 z-[12010]">
                      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={closeDocViewer} />
                      <div className="absolute inset-0 z-[12011] flex flex-col">
                        <div className="px-3 py-2 flex items-center justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="text-xs text-site-text/90 truncate">{fileDownloadName}</div>
                            <div className="text-[11px] text-site-text/60 truncate">{formatBytes(fileAttRaw?.size || 0)}</div>
                          </div>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              closeDocViewer();
                            }}
                            className="w-10 h-10 rounded-full glass-btn text-site-text/90 hover:text-site-text flex items-center justify-center transition active:scale-[0.92]"
                            title="Закрыть"
                            aria-label="Закрыть"
                          >
                            ✕
                          </button>
                        </div>

                        <div
                          className="flex-1 min-h-0 overflow-hidden px-2 pb-2"
                          onTouchStart={(e) => {
                            // Комментарий на русском: pinch-to-zoom и панорамирование одним пальцем при зуме.
                            if (e.touches.length === 2) {
                              const t1 = e.touches[0];
                              const t2 = e.touches[1];
                              const dist = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
                              const cx = (t1.clientX + t2.clientX) / 2;
                              const cy = (t1.clientY + t2.clientY) / 2;
                              const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                              const p = { x: cx - rect.left, y: cy - rect.top };
                              docViewerPinchRef.current = {
                                distance: dist,
                                point: p,
                                initialScale: docViewerScale,
                                initialTranslate: { ...docViewerTranslate },
                              };
                              docViewerTouchIdRef.current = null;
                              docViewerTouchStartRef.current = null;
                              return;
                            }
                            if (e.touches.length === 1 && docViewerScale > 1) {
                              const t = e.touches[0];
                              docViewerTouchIdRef.current = t.identifier;
                              docViewerTouchStartRef.current = { x: t.clientX, y: t.clientY };
                            }
                          }}
                          onTouchMove={(e) => {
                            if (e.touches.length === 2 && docViewerPinchRef.current) {
                              e.preventDefault();
                              const t1 = e.touches[0];
                              const t2 = e.touches[1];
                              const dist = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
                              const cx = (t1.clientX + t2.clientX) / 2;
                              const cy = (t1.clientY + t2.clientY) / 2;
                              const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                              const p = { x: cx - rect.left, y: cy - rect.top };
                              const pinch = docViewerPinchRef.current;
                              const s0 = Math.max(1, Number(pinch.initialScale || 1) || 1);
                              const nextScale = Math.max(1, Math.min(4, (s0 * dist) / Math.max(1, pinch.distance)));
                              setDocViewerScale(nextScale);
                              if (nextScale <= 1) {
                                setDocViewerTranslate({ x: 0, y: 0 });
                                return;
                              }
                              const ratio = nextScale / s0;
                              const t0 = pinch.initialTranslate;
                              // Комментарий на русском: удерживаем точку под пальцами при смене масштаба.
                              setDocViewerTranslate({
                                x: p.x - (p.x - t0.x) * ratio,
                                y: p.y - (p.y - t0.y) * ratio,
                              });
                              return;
                            }
                            if (e.touches.length === 1 && docViewerScale > 1) {
                              e.preventDefault();
                              const t = e.touches[0];
                              if (docViewerTouchIdRef.current !== t.identifier) return;
                              const st = docViewerTouchStartRef.current;
                              if (!st) return;
                              const dx = t.clientX - st.x;
                              const dy = t.clientY - st.y;
                              setDocViewerTranslate((cur) => ({ x: cur.x + dx, y: cur.y + dy }));
                              docViewerTouchStartRef.current = { x: t.clientX, y: t.clientY };
                            }
                          }}
                          onTouchEnd={(e) => {
                            if (e.touches.length < 2) docViewerPinchRef.current = null;
                            if (e.touches.length === 0) {
                              docViewerTouchIdRef.current = null;
                              docViewerTouchStartRef.current = null;
                            }
                          }}
                          onTouchCancel={() => {
                            docViewerPinchRef.current = null;
                            docViewerTouchIdRef.current = null;
                            docViewerTouchStartRef.current = null;
                          }}
                          onPointerDown={(e) => {
                            if (docViewerScale <= 1) return;
                            docViewerPointerIdRef.current = e.pointerId;
                            docViewerStartRef.current = { x: e.clientX, y: e.clientY };
                            docViewerDraggingRef.current = true;
                            try { (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId); } catch {}
                          }}
                          onPointerMove={(e) => {
                            if (!docViewerDraggingRef.current) return;
                            if (docViewerPointerIdRef.current !== e.pointerId) return;
                            const st = docViewerStartRef.current;
                            if (!st) return;
                            const dx = e.clientX - st.x;
                            const dy = e.clientY - st.y;
                            setDocViewerTranslate((t) => ({ x: t.x + dx, y: t.y + dy }));
                            docViewerStartRef.current = { x: e.clientX, y: e.clientY };
                          }}
                          onPointerUp={(e) => {
                            if (docViewerPointerIdRef.current !== e.pointerId) return;
                            try { (e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId); } catch {}
                            docViewerPointerIdRef.current = null;
                            docViewerStartRef.current = null;
                            docViewerDraggingRef.current = false;
                          }}
                          onPointerCancel={() => {
                            docViewerPointerIdRef.current = null;
                            docViewerStartRef.current = null;
                            docViewerDraggingRef.current = false;
                          }}
                        >
                          <div
                            className="w-full h-full rounded-xl border border-site-border bg-black/20 overflow-hidden"
                            style={{
                              // Комментарий на русском: при масштабе 1x даём скроллить документ, при зуме — управляем жестами сами.
                              touchAction: docViewerScale > 1 ? 'none' : 'pan-y',
                            }}
                          >
                            {docViewerKind === 'pdf' ? (
                                <div className="w-full h-full flex flex-col">
                                  <div className="px-3 py-2 flex items-center justify-between gap-2 text-[11px] text-site-text/80">
                                    <div className="flex items-center gap-2">
                                      <button
                                        type="button"
                                        onClick={() => setDocViewerPdfPage((p) => Math.max(1, (Number(p || 1) || 1) - 1))}
                                        disabled={docViewerPdfPage <= 1 || docViewerPdfLoading}
                                        className="px-2 py-1 rounded-lg border border-site-border bg-black/20 disabled:opacity-50"
                                        title="Предыдущая страница"
                                      >
                                        ←
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => setDocViewerPdfPage((p) => Math.min(Math.max(1, docViewerPdfPages || 1), (Number(p || 1) || 1) + 1))}
                                        disabled={docViewerPdfPages > 0 ? docViewerPdfPage >= docViewerPdfPages || docViewerPdfLoading : true}
                                        className="px-2 py-1 rounded-lg border border-site-border bg-black/20 disabled:opacity-50"
                                        title="Следующая страница"
                                      >
                                        →
                                      </button>
                                      <span className="tabular-nums">
                                        {docViewerPdfPages > 0 ? `${docViewerPdfPage}/${docViewerPdfPages}` : 'PDF'}
                                      </span>
                                    </div>
                                    {docViewerPdfLoading ? <span className="opacity-70">Загрузка...</span> : null}
                                  </div>
                                  <div className="flex-1 min-h-0 overflow-auto">
                                    <div className="w-full flex items-start justify-center p-2">
                                      <canvas ref={docViewerPdfCanvasRef} className="max-w-full h-auto rounded-lg border border-site-border bg-black/30" />
                                    </div>
                                  </div>
                                </div>
                              ) : docViewerKind === 'docx' || docViewerKind === 'xlsx' ? (
                                <div className="w-full h-full flex flex-col">
                                  <div className="px-3 py-2 flex items-center justify-between gap-2 text-[11px] text-site-text/80">
                                    <div className="flex items-center gap-2">
                                      <button
                                        type="button"
                                        onClick={() => scrollDocToPage(Math.max(1, docViewerDocPage - 1))}
                                        disabled={docViewerDocPage <= 1}
                                        className="px-2 py-1 rounded-lg border border-site-border bg-black/20 disabled:opacity-50"
                                        title="Предыдущая страница"
                                      >
                                        ←
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => scrollDocToPage(Math.min(docViewerDocPages, docViewerDocPage + 1))}
                                        disabled={docViewerDocPage >= docViewerDocPages}
                                        className="px-2 py-1 rounded-lg border border-site-border bg-black/20 disabled:opacity-50"
                                        title="Следующая страница"
                                      >
                                        →
                                      </button>
                                      <span className="tabular-nums">{`${docViewerDocPage}/${docViewerDocPages}`}</span>
                                    </div>
                                    <span className="opacity-70">{docViewerKind.toUpperCase()}</span>
                                  </div>

                                  <div
                                    ref={docViewerDocScrollRef}
                                    className={`flex-1 min-h-0 ${docViewerScale > 1 ? 'overflow-hidden' : 'overflow-auto'}`}
                                    onScroll={() => {
                                      const el = docViewerDocScrollRef.current;
                                      if (!el) return;
                                      if (docViewerDocRafRef.current != null) return;
                                      docViewerDocRafRef.current = requestAnimationFrame(() => {
                                        docViewerDocRafRef.current = null;
                                        const h = Math.max(1, Number(docViewerDocPageHRef.current || el.clientHeight || 1) || 1);
                                        const p = Math.max(1, Math.min(docViewerDocPages, Math.floor((el.scrollTop || 0) / h) + 1));
                                        setDocViewerDocPage(p);
                                      });
                                    }}
                                  >
                                    <div
                                      className="w-full h-full"
                                      style={{
                                        // Комментарий на русском: на 1x НЕ используем transform (иначе iOS ломает scroll).
                                        transform:
                                          docViewerScale > 1
                                            ? `translate3d(${docViewerTranslate.x}px, ${docViewerTranslate.y}px, 0) scale(${docViewerScale})`
                                            : 'none',
                                        transformOrigin: '0 0',
                                        transition: docViewerDraggingRef.current ? 'none' : 'transform 120ms ease-out',
                                      }}
                                    >
                                      <div className="w-full p-3 text-site-text">
                                        {docViewerKind === 'xlsx' ? (
                                          docViewerTable.length ? (
                                            <div className="max-w-full overflow-auto rounded-lg border border-site-border">
                                              <table className="min-w-full border-collapse text-xs">
                                                <tbody>
                                                  {docViewerTable.map((row, rowIndex) => (
                                                    <tr key={rowIndex}>
                                                      {row.map((cell, columnIndex) => (
                                                        <td
                                                          key={columnIndex}
                                                          className="max-w-[320px] whitespace-pre-wrap break-words border border-site-border px-2 py-1.5 align-top"
                                                        >
                                                          {cell}
                                                        </td>
                                                      ))}
                                                    </tr>
                                                  ))}
                                                </tbody>
                                              </table>
                                            </div>
                                          ) : <p className="text-site-text/60">Таблица пуста</p>
                                        ) : (
                                          <div
                                            className="prose prose-invert max-w-none"
                                            dangerouslySetInnerHTML={{ __html: docViewerHtml || '<p>Загрузка...</p>' }}
                                          />
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-site-text/70 text-sm">
                                  Не удалось открыть файл
                                </div>
                              )}
                          </div>
                        </div>

                        <div className="px-3 pb-3 pt-2 flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => {
                                setDocViewerScale((s) => {
                                  const next = Math.max(1, Math.min(4, s - 0.5));
                                  if (next <= 1) setDocViewerTranslate({ x: 0, y: 0 });
                                  return next;
                                });
                              }}
                              className="w-11 h-11 rounded-full glass-btn text-site-text/90 hover:text-site-text flex items-center justify-center transition active:scale-[0.92] text-xl font-bold"
                              title="Уменьшить"
                              aria-label="Уменьшить"
                            >
                              −
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setDocViewerScale(1);
                                setDocViewerTranslate({ x: 0, y: 0 });
                              }}
                              className="w-11 h-11 rounded-full glass-btn text-site-text/90 hover:text-site-text flex items-center justify-center transition active:scale-[0.92]"
                              title="Сброс"
                              aria-label="Сброс"
                            >
                              1×
                            </button>
                            <button
                              type="button"
                              onClick={() => setDocViewerScale((s) => Math.min(4, s + 0.5))}
                              className="w-11 h-11 rounded-full glass-btn text-site-text/90 hover:text-site-text flex items-center justify-center transition active:scale-[0.92] text-xl font-bold"
                              title="Увеличить"
                              aria-label="Увеличить"
                            >
                              +
                            </button>
                          </div>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              void downloadFile();
                            }}
                            className="w-11 h-11 rounded-full glass-btn text-site-text/90 hover:text-site-text flex items-center justify-center transition active:scale-[0.92]"
                            title="Скачать"
                            aria-label="Скачать"
                          >
                            <UiIcon name="ArrowDown" />
                          </button>
                        </div>
                      </div>
                    </div>,
                    document.body
                  )
                : null)}
            {shouldShowFileCaption ? (
              <div
                className="mt-2 px-1 whitespace-pre-wrap break-words select-text"
                style={{ fontSize: 'var(--dm-font,0.875rem)', lineHeight: 'var(--dm-leading,1.5)' }}
                dangerouslySetInnerHTML={{ __html: formattedText }}
              />
            ) : null}
          </div>
        ) : formattedText ? (
          <div
            className="whitespace-pre-wrap break-words select-text"
            style={{ fontSize: 'var(--dm-font,0.875rem)', lineHeight: 'var(--dm-leading,1.5)' }}
            dangerouslySetInnerHTML={{ __html: formattedText }}
          />
        ) : (
          <div
            className="whitespace-pre-wrap break-words select-text"
            style={{ fontSize: 'var(--dm-font,0.875rem)', lineHeight: 'var(--dm-leading,1.5)' }}
          >
            {rawText || '[Пустое сообщение]'}
          </div>
        )}

        {/* Комментарий на русском: резервируем одинаковую высоту для loading и готового preview — иначе при появлении данных элемент "прыгает". */}
        {!isVoice && (linkPreviewLoading || linkPreview) && firstUrl && (
          <div
            className={`mt-2 block rounded-xl border border-site-border bg-site-text/5 overflow-hidden ${
              linkPreview?.kind === 'video' ? 'h-[168px]' : 'h-[68px]'
            }`}
          >
            {linkPreview ? (
              <a
                href={linkPreview.url}
                target="_blank"
                rel="noopener noreferrer external"
                className="block h-full hover:bg-site-text/5 transition-colors"
              >
                {linkPreview.kind === 'video' ? (
                  <div className="h-full flex flex-col">
                    <div className="relative w-full h-[108px] bg-site-surface/60 overflow-hidden">
                      {linkPreview.image ? (
                        <img src={linkPreviewImageSrc || linkPreview.image} alt="" className="w-full h-full object-cover" loading="lazy" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-sm text-site-text/70">{linkPreview.domain || 'видео'}</div>
                      )}
                      <div className="absolute inset-0 flex items-center justify-center bg-black/15" aria-hidden="true">
                        <span className="w-11 h-11 rounded-full bg-black/40 border border-site-border flex items-center justify-center text-site-text/95">
                          <UiIcon name="Play" />
                        </span>
                      </div>
                    </div>
                    <div className="px-3 py-2 flex-1 min-w-0">
                      <div className="text-sm text-site-text/90 truncate">{linkPreview.title || linkPreview.domain || 'Видео'}</div>
                      {linkPreview.description && (
                        <div className="mt-0.5 text-xs text-site-text/70 line-clamp-2">{linkPreview.description}</div>
                      )}
                      <div className="mt-0.5 text-[11px] text-site-accent/80 truncate">{linkPreview.domain}</div>
                    </div>
                  </div>
                ) : (
                  <div className="flex gap-3 h-full">
                    <div className="relative w-16 h-16 bg-site-surface/60 flex items-center justify-center text-sm text-site-text/70 shrink-0 overflow-hidden">
                      {linkPreview.image ? (
                        <img src={linkPreviewImageSrc || linkPreview.image} alt="" className="w-full h-full object-cover" loading="lazy" />
                      ) : (
                        <span className="px-2 text-center break-words leading-4">{linkPreview.domain || 'ссылка'}</span>
                      )}
                    </div>
                    <div className="py-2 pr-3 flex-1 min-w-0">
                      <div className="text-sm text-site-text/90 truncate">{linkPreview.title || linkPreview.domain || 'Ссылка'}</div>
                      {linkPreview.description && (
                        <div className="mt-0.5 text-xs text-site-text/70 line-clamp-2">{linkPreview.description}</div>
                      )}
                      <div className="mt-0.5 text-[11px] text-site-accent/80 truncate">{linkPreview.domain}</div>
                    </div>
                  </div>
                )}
              </a>
            ) : (
              <div className="flex gap-3 h-full items-center">
                <div className={`bg-site-surface/40 shrink-0 flex items-center justify-center ${linkPreview?.kind === 'video' ? 'w-full h-[108px]' : 'w-16 h-16'}`}>
                  <span className="w-5 h-5 border-2 border-site-border border-t-white/60 rounded-full animate-spin" />
                </div>
                <div className="py-2 pr-3 flex-1 min-w-0">
                  <div className="text-xs text-site-muted">Загружаем превью...</div>
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* Время и статус: для медиа (фото/gif/видео) рисуем внутри контента, поэтому здесь скрываем. */}
        {!isMedia && (
          <div className={`flex items-center justify-end gap-1 mt-1 text-xs ${
            isOwn ? 'text-site-accent/70' : 'text-site-muted'
          }`}>
            <span>{timeString}</span>
            {isEdited && <span className="text-[10px] text-site-secondary/80">изменено</span>}
            {isOwn && !!statusIcon && (
              <span className={`ml-0.5 font-semibold ${status === 'read' ? 'text-site-accent' : status === 'error' ? 'text-site-danger' : ''}`}>
                {statusIcon}
              </span>
            )}
          </div>
        )}

        {/* Реакции */}
      {/* Комментарий на русском: реакции рисуем в левом углу сообщения, слегка “вылезая” за пузырёк. */}
      {hasReactions && (
        <div className="absolute left-0 top-full -translate-x-2 -translate-y-2 z-30 flex flex-wrap gap-2">
          {reactions.map((r: any) => {
            const emo = String(r?.emoji || '').trim();
            if (!emo) return null;
            const cnt = Number(r?.count || 0) || 0;
            const me = !!r?.me;
            return (
              <button
                key={emo}
                type="button"
                onClick={() => applyReaction(emo)}
                className={`lisad-react-pill h-10 px-3 rounded-full flex items-center gap-1.5 transition-transform active:scale-[0.95] ${
                  me ? 'lisad-react-pill--me' : ''
                }`}
                title="Поставить/снять реакцию"
                aria-label="Реакция"
              >
                <span className="text-[20px] leading-none">{emo}</span>
                {cnt > 1 ? <span className="text-[13px] font-semibold text-site-text/90 tabular-nums">{cnt}</span> : null}
              </button>
            );
          })}
        </div>
      )}
        
        {/* Контекстное меню */}
        {showMenu &&
          (typeof document !== 'undefined' && document.body
            ? createPortal(
                <>
                  {/* Комментарий на русском: отдельное компактное меню реакций (в один ряд + горизонтальная прокрутка). */}
                  {onReact && (
                    <div
                      ref={reactionsMenuRef}
                      className="fixed z-[9999] glass-subtle rounded-full shadow-[0_12px_40px_rgba(0,0,0,0.45)] overflow-hidden min-w-[220px] max-w-[min(82vw,360px)]"
                      style={
                        reactionsMenuPos
                          ? { left: reactionsMenuPos.left, top: reactionsMenuPos.top }
                          : { left: 0, top: 0, opacity: 0, pointerEvents: 'none' }
                      }
                    >
                      <div
                        className="flex items-center gap-1 px-2 py-1 overflow-x-auto"
                        style={{ WebkitOverflowScrolling: 'touch' }}
                      >
                        {quickReactions.map((emo) => (
                          <button
                            key={emo}
                            type="button"
                            onClick={() => {
                              applyReaction(emo);
                              closeMenu();
                            }}
                            className="w-12 h-12 rounded-full hover:bg-site-text/10 transition-colors flex items-center justify-center text-2xl shrink-0"
                            aria-label={`Реакция ${emo}`}
                            title="Реакция"
                          >
                            {emo}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <div
                    ref={menuRef}
                    className="fixed z-[9999] glass-strong rounded-xl sm:rounded-2xl shadow-[0_20px_70px_rgba(0,0,0,0.55)] overflow-hidden w-[min(92vw,280px)] sm:min-w-[240px]"
                    style={menuPos ? { left: menuPos.left, top: menuPos.top } : { left: 0, top: 0, opacity: 0, pointerEvents: 'none' }}
                  >
                    <button
                      type="button"
                      disabled={!onReply}
                      onClick={() => {
                        if (!onReply) return;
                        haptic(10);
                        onReply(message);
                        closeMenu();
                      }}
                      className={`w-full px-3 py-2 sm:px-3.5 sm:py-2.5 text-[13px] sm:text-sm hover:bg-site-text/6 transition-colors flex items-center justify-between gap-3 ${
                        onReply ? 'text-site-text' : 'text-site-muted cursor-not-allowed'
                      }`}
                      title="Ответить"
                      aria-label="Ответить на сообщение"
                    >
                      <span className="font-medium">Ответить</span>
                      <svg viewBox="0 0 24 24" className={`w-4 h-4 shrink-0 ${onReply ? 'text-site-text' : 'text-site-muted'}`} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M9 14 4 9l5-5" />
                        <path d="M4 9h11a5 5 0 0 1 0 10h-1" />
                      </svg>
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        haptic(8);
                        void copyMessageToClipboard();
                        closeMenu();
                      }}
                      className="w-full px-3 py-2 sm:px-3.5 sm:py-2.5 text-[13px] sm:text-sm text-site-text hover:bg-site-text/6 transition-colors flex items-center justify-between gap-3"
                      title="Скопировать"
                      aria-label="Скопировать сообщение"
                    >
                      <span className="font-medium">Скопировать</span>
                      <svg viewBox="0 0 24 24" className="w-4 h-4 shrink-0 text-site-text" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <rect x="9" y="9" width="13" height="13" rx="2" />
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                      </svg>
                    </button>

                    <button
                      type="button"
                      disabled={!canEditAction}
                      onClick={() => {
                        if (!canEditAction) return;
                        haptic(8);
                        onEdit?.(message);
                        closeMenu();
                      }}
                      className={`w-full px-3 py-2 sm:px-3.5 sm:py-2.5 text-[13px] sm:text-sm hover:bg-site-text/6 transition-colors flex items-center justify-between gap-3 ${
                        canEditAction ? 'text-site-text' : 'text-site-muted cursor-not-allowed'
                      }`}
                      title="Изменить"
                      aria-label="Изменить сообщение"
                    >
                      <span className="font-medium">Изменить</span>
                      <svg viewBox="0 0 24 24" className={`w-4 h-4 shrink-0 ${canEditAction ? 'text-site-text' : 'text-site-muted'}`} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M12 20h9" />
                        <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
                      </svg>
                    </button>

                    <button
                      type="button"
                      disabled={!onForward}
                      onClick={() => {
                        if (!onForward) return;
                        haptic(8);
                        onForward(message);
                        closeMenu();
                      }}
                      className={`w-full px-3 py-2 sm:px-3.5 sm:py-2.5 text-[13px] sm:text-sm hover:bg-site-text/6 transition-colors flex items-center justify-between gap-3 ${
                        onForward ? 'text-site-text' : 'text-site-muted cursor-not-allowed'
                      }`}
                      title="Переслать"
                      aria-label="Переслать сообщение"
                    >
                      <span className="font-medium">Переслать</span>
                      <svg viewBox="0 0 24 24" className={`w-4 h-4 shrink-0 ${onForward ? 'text-site-text' : 'text-site-muted'}`} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M5 12h14" />
                        <path d="M13 6l6 6-6 6" />
                      </svg>
                    </button>

                    <button
                      type="button"
                      disabled={!onDelete}
                      onClick={() => {
                        if (!onDelete) return;
                        haptic(10);
                        closeMenu();
                        setShowDeleteConfirm(true);
                      }}
                      className={`w-full px-3 py-2 sm:px-3.5 sm:py-2.5 text-[13px] sm:text-sm hover:bg-site-text/6 transition-colors flex items-center justify-between gap-3 ${
                        onDelete ? 'text-site-danger' : 'text-site-muted cursor-not-allowed'
                      }`}
                      title="Удалить"
                      aria-label="Удалить сообщение"
                    >
                      <span className="font-semibold">Удалить</span>
                      <svg viewBox="0 0 24 24" className={`w-4 h-4 shrink-0 ${onDelete ? 'text-site-danger' : 'text-site-muted'}`} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M3 6h18" />
                        <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                        <path d="M10 11v6" />
                        <path d="M14 11v6" />
                      </svg>
                    </button>

                    <div className="h-px bg-site-text/10 my-1" aria-hidden="true" />

                    <button
                      type="button"
                      disabled={!canPinAction}
                      onClick={() => {
                        if (!canPinAction) return;
                        haptic(8);
                        onPin?.(message);
                        closeMenu();
                      }}
                      className={`w-full px-3 py-2 sm:px-3.5 sm:py-2.5 text-[13px] sm:text-sm hover:bg-site-text/6 transition-colors flex items-center justify-between gap-3 ${
                        canPinAction ? 'text-site-text' : 'text-site-muted cursor-not-allowed'
                      }`}
                      title={isPinned ? 'Открепить' : 'Закрепить'}
                      aria-label={isPinned ? 'Открепить сообщение' : 'Закрепить сообщение'}
                    >
                      <span className="font-medium">{isPinned ? 'Открепить' : 'Закрепить'}</span>
                      <svg viewBox="0 0 24 24" className={`w-4 h-4 shrink-0 ${canPinAction ? 'text-site-text' : 'text-site-muted'}`} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
                      </svg>
                    </button>

                    <button
                      type="button"
                      disabled={!onToggleSelect}
                      onClick={() => {
                        if (!onToggleSelect) return;
                        haptic(8);
                        onToggleSelect(message);
                        closeMenu();
                      }}
                      className={`w-full px-3 py-2 sm:px-3.5 sm:py-2.5 text-[13px] sm:text-sm hover:bg-site-text/6 transition-colors flex items-center justify-between gap-3 ${
                        onToggleSelect ? 'text-site-text' : 'text-site-muted cursor-not-allowed'
                      }`}
                      title="Выбрать"
                      aria-label="Выбрать сообщение"
                    >
                      <span className="font-medium">{isSelected ? 'Снять выбор' : 'Выбрать'}</span>
                      <svg viewBox="0 0 24 24" className={`w-4 h-4 shrink-0 ${onToggleSelect ? 'text-site-text' : 'text-site-muted'}`} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <rect x="3" y="3" width="18" height="18" rx="2" />
                        {isSelected ? <path d="M7 12l3 3 7-7" /> : <path d="M8 12h8" />}
                      </svg>
                    </button>
                  </div>
                </>,
                document.body
              )
            : null)}

        {/* Комментарий на русском: уточнялка удаления (у себя / у всех). */}
        {showDeleteConfirm &&
          onDelete &&
          (typeof document !== 'undefined' && document.body
            ? createPortal(
                <>
                  <div className="fixed inset-0 z-[10000] bg-black/60 backdrop-blur-sm" onClick={closeDeleteConfirm} />
                  <div className="fixed inset-0 z-[10001] flex items-center justify-center px-4">
                    <div className="w-full max-w-sm glass-strong rounded-2xl border border-site-border shadow-[0_25px_90px_rgba(0,0,0,0.65)] overflow-hidden">
                      <div className="px-4 py-3 text-sm font-semibold text-site-text">Удалить сообщение?</div>
                      <div className="px-4 pb-4 flex flex-col gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            onDelete(message.id, false);
                            closeDeleteConfirm();
                          }}
                          className="w-full px-3 py-2 rounded-xl glass-subtle text-site-text hover:bg-site-text/6 transition-colors flex items-center justify-center gap-2"
                        >
                          <span className="w-8 h-8 rounded-full glass-btn flex items-center justify-center"><UiIcon name="Trash" /></span>
                          <span className="font-semibold">{isUndeliveredVisual ? 'Удалить (не отправлено)' : 'У себя'}</span>
                        </button>
                        {/* Комментарий на русском: для неотправленного сообщения “У всех” бессмысленно — оно ещё не на сервере. */}
                        {isOwn && !isUndeliveredVisual && (
                          <button
                            type="button"
                            onClick={() => {
                              onDelete(message.id, true);
                              closeDeleteConfirm();
                            }}
                            className="w-full px-3 py-2 rounded-xl glass-subtle text-site-danger hover:bg-site-text/6 transition-colors flex items-center justify-center gap-2"
                          >
                            <span className="w-8 h-8 rounded-full glass-btn flex items-center justify-center"><UiIcon name="Warning" /></span>
                            <span className="font-semibold">У всех</span>
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={closeDeleteConfirm}
                          className="w-full px-3 py-2 rounded-xl glass-subtle text-site-text hover:bg-site-text/6 transition-colors"
                        >
                          Отмена
                        </button>
                      </div>
                    </div>
                  </div>
                </>,
                document.body
              )
            : null)}
      </div>
      </div>
      
      {/* Кнопка удаления при наведении */}
      {onDelete && (
        <button
          type="button"
          onClick={() => setShowMenu(!showMenu)}
          className={`opacity-0 group-hover:opacity-100 transition-opacity ${
            isOwn ? 'order-first mr-2' : 'ml-2'
          } text-site-muted hover:text-site-secondary self-center`}
          title="Действия с сообщением"
          aria-label="Действия с сообщением"
        >
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
          </svg>
        </button>
      )}
    </div>
  );
});

export default MessageBubble;


/**
 * Компонент индикатора набора текста.
 */
export const TypingIndicator = memo(function TypingIndicator({
  userName
}: { userName: string }) {
  return (
    <div className="flex items-center gap-2 px-4 py-2 text-sm text-site-muted">
      <div className="flex gap-1">
        <span className="w-2 h-2 bg-site-raised rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
        <span className="w-2 h-2 bg-site-raised rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
        <span className="w-2 h-2 bg-site-raised rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
      </div>
      <span>{userName} печатает...</span>
    </div>
  );
});


/**
 * Компонент разделителя по дате.
 */
export const DateSeparator = memo(function DateSeparator({
  date
}: { date: string }) {
  const formattedDate = useMemo(() => {
    const d = new Date(date);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (d.toDateString() === today.toDateString()) {
      return 'Сегодня';
    } else if (d.toDateString() === yesterday.toDateString()) {
      return 'Вчера';
    } else {
      return d.toLocaleDateString('ru-RU', { 
        day: 'numeric', 
        month: 'long',
        year: d.getFullYear() !== today.getFullYear() ? 'numeric' : undefined
      });
    }
  }, [date]);
  
  return (
    <div className="flex items-center justify-center my-4">
      <div className="px-3 py-1 bg-site-raised/50 rounded-full text-xs text-site-muted">
        {formattedDate}
      </div>
    </div>
  );
});


/**
 * Skeleton для загрузки сообщений.
 */
export const MessageSkeleton = memo(function MessageSkeleton({
  isOwn = false
}: { isOwn?: boolean }) {
  return (
    <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'} mt-3`}>
      {!isOwn && (
        <div className="w-8 h-8 rounded-full bg-site-raised animate-pulse mr-2" />
      )}
      <div className={`
        ${isOwn ? 'bg-site-raised' : 'bg-site-raised'}
        rounded-2xl px-4 py-3 animate-pulse
      `}>
        <div className="h-4 bg-site-raised rounded w-32 mb-2" />
        <div className="h-3 bg-site-raised rounded w-20" />
      </div>
    </div>
  );
});




